/* ──────────────────────────────────────────────────────────────
   Editor/index.tsx · Live item editor (sk-catalog-api /items)
   Loads an item + its type's field definitions, edits field values,
   and drives the lifecycle: Save (PATCH) · Publish · Deactivate · Versions.
   ────────────────────────────────────────────────────────────── */

import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useReloadOnFocus } from '../../navigation/useReloadOnFocus';
import {
  View,
  Text,
  Image,
  ScrollView,
  TextInput,
  TouchableOpacity,
  ActivityIndicator,
  Platform,
  ViewStyle,
} from 'react-native';
// Bare axios (NOT the shared axiosInstance) for the direct-to-storage media PUT:
// the presigned URL points at S3/GCS, so it must NOT carry our auth cookies or
// JSON interceptors (withCredentials:false, raw body) or the signature breaks.
import axios from 'axios';

import { useTheme } from '../../context/themeContext';
import { useNavigation, useRoute, type RouteProp } from '@react-navigation/native';
import type { NativeStackNavigationProp } from '@react-navigation/native-stack';
import type { AppStackParamList } from '../../navigation/types';
import CommonInput from '../../components/commonInput';
import { useFieldTypes } from '../../hooks/useFieldTypes';
import FieldControl from './fieldControl';
import CatalogManager from './catalogManager';
import BundleManager from '../bundleManager';
import { type FieldDef } from '../../data/mockData';
import {
  Icon,
  Button,
  Chip,
  Switch,
  AppDropdown,
  StateBadge,
  Modal,
  Empty,
  Popover,
  MenuItem,
  MenuSeparator,
  ConfirmDialog,
  useToast,
} from '../../components/ui';
import {
  getItem,
  updateItem,
  publishItem,
  deactivateItem,
  getItemVersions,
  getItemVariants,
  resolveItem,
  exportItem,
  createItem,
  getItems,
} from '../../services/catalog/item';
import { getItemTypeFields, getItemType, addItemTypeField, deleteItemTypeField, updateItemTypeField } from '../../services/catalog/itemType';
import {
  getCategories,
  getTags,
  createCategory,
  createTag,
  updateCategory,
  updateTag,
  deleteCategory,
  deleteTag,
  setItemCategories,
  setItemTags,
  getCollections,
  getCollection,
  createCollection,
  updateCollection,
  deleteCollection,
  addCollectionItems,
  removeCollectionItem,
  reorderCollectionItems,
} from '../../services/catalog/taxonomy';
import { getLocations, createLocation, updateLocation, deleteLocation } from '../../services/catalog/location';
import { getComponents, addComponent, updateComponent, removeComponent, relinkPrice } from '../../services/catalog/bundle';
import {
  getSuggestions,
  acceptSuggestion,
  rejectSuggestion,
} from '../../services/catalog/suggestion';
import { generateSuggestions } from '../../services/catalog/aiSuggestion';
import { getPriceImpact, commitPrice } from '../../services/catalog/bundle';
import {
  initiateUpload,
  completeUpload,
  getMediaStatus,
  clearUpload,
} from '../../services/catalog/media';
import {
  handleApiError,
  isHsnSacRequiredError,
  isFieldLockedError,
  getLockedFieldKey,
  isIllegalTransitionError,
  getIllegalTransitionMeta,
  isPriceFieldNotLockableError,
} from '../../utils/errorHandler';
// SKCATALOG-CC-002 CR-1/CR-2/CR-5/CR-7 — console side (SKCATALOG-DB-001).
import { getBasePrice, getBasePriceHistory } from '../../services/catalog/basePrice';
import type { BasePriceRow, BasePriceRead } from '../../schemas/catalog/basePrice';
import { formatMoney } from '../../utils/money';
import { isLockableField, LOCKED_HELPER_TEXT } from '../../utils/fieldLock';
import { taxabilityOf, hasHsnSac, HSN_REQUIRED_MESSAGE } from '../../utils/taxability';
import { allowedTransitions, isSellableState, notSellableReason } from '../../utils/lifecycle';
import { showSuccess } from '../../utils/successHandler';
import { useQuota } from '../../context/quotaContext';
import type { CatalogItem } from '../../schemas/catalog/item';
import type { FieldDefinition } from '../../schemas/catalog/itemType';
import type { Category, Tag, Collection } from '../../schemas/catalog/taxonomy';
import type { Suggestion } from '../../schemas/catalog/suggestion';
import type { RelationType } from '../../schemas/catalog/bundle';

// Component relation kinds — matches the standalone Bundles screen.
const RELATIONS: RelationType[] = ['BUNDLE_DYNAMIC', 'BUNDLE_STATIC', 'ACCESSORY', 'SUBSTITUTE'];

// Pull base_price (global, non-location) out of a fully-fetched item.
const extractPrice = (it: any): number | null => {
  const fv = (it?.fieldValues || []).find(
    (f: any) => (f.fieldDefinition?.fieldKey ?? f.fieldKey) === 'base_price' && !f.locationId,
  );
  const n = Number(fv?.valueJson ?? fv?.value);
  return Number.isFinite(n) ? n : null;
};

export default function ItemEditor() {
  const toast = useToast();
  const navigation = useNavigation<NativeStackNavigationProp<AppStackParamList>>();
  const { refresh: refreshQuota } = useQuota();
  const route = useRoute<RouteProp<AppStackParamList, 'editor'>>();
  // Route params: { id?, type? }. Default to {} so the existing param reads
  // (params.id / params.type) keep working unchanged.
  const params = route.params ?? {};
  const { theme, isDark } = useTheme();
  const { colors, fontSize, fontWeight, font, radii } = theme;
  // DB-backed field-type catalog (falls back to the local registry).
  const fieldTypes = useFieldTypes();

  const isNew = params.id === 'new';

  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [item, setItem] = useState<CatalogItem | null>(null);
  const [typeName, setTypeName] = useState<string>('Item');
  const [fields, setFields] = useState<FieldDefinition[]>([]);
  const [vals, setVals] = useState<Record<string, any>>({});
  const [sku, setSku] = useState('');
  const [state, setState] = useState<string>('DRAFT');
  const [dirty, setDirty] = useState(false);

  /* ── SKCATALOG-CC-002 CR-1 · HSN/SAC (B-D7) ────────────────────────────────
     `hsnBlocked` drives the INLINE, actionable block SKCATALOG-DB-001 §1 asks
     for. It is deliberately not a toast: a toast is dismissed and forgotten,
     and the operator needs the message sitting next to the input they have to
     fill in. Set both when the console can see the code is missing (so the
     block appears before they even try) and when the API answers 422. */
  const [hsnSac, setHsnSac] = useState('');
  const [hsnBlocked, setHsnBlocked] = useState(false);

  /* ── CC-002 CR-5 · dated base price (CAT-D2) ───────────────────────────── */
  const [basePrice, setBasePrice] = useState<BasePriceRead | null>(null);
  const [priceHistory, setPriceHistory] = useState<BasePriceRow[] | null>(null);
  const [priceHistoryOpen, setPriceHistoryOpen] = useState(false);
  const [loadingPriceHistory, setLoadingPriceHistory] = useState(false);

  /* ── CC-002 CR-2 · brand field locks (P02) ─────────────────────────────── */
  const [lockingField, setLockingField] = useState<string | null>(null);
  // Confirm-before-save: clicking Save opens a review dialog first so the user
  // can confirm the field values they're about to commit.
  const [confirmSaveOpen, setConfirmSaveOpen] = useState(false);
  const [deactOpen, setDeactOpen] = useState(false);
  const [deactReason, setDeactReason] = useState('');
  const [versions, setVersions] = useState<any[] | null>(null);

  // Taxonomy assignment
  const [allCategories, setAllCategories] = useState<Category[]>([]);
  const [allTags, setAllTags] = useState<Tag[]>([]);
  const [selCats, setSelCats] = useState<Set<string>>(new Set());
  const [selTags, setSelTags] = useState<Set<string>>(new Set());
  // Tag names the item already has — resolved to tag ids once allTags loads.
  const [assignedTagNames, setAssignedTagNames] = useState<string[]>([]);
  const [savingTax, setSavingTax] = useState(false);
  // Inline "create new" for categories / tags.
  const [catName, setCatName] = useState('');
  const [catParentId, setCatParentId] = useState(''); // '' = top-level category
  const [tagName, setTagName] = useState('');
  const [addingCat, setAddingCat] = useState(false);
  const [addingTag, setAddingTag] = useState(false);
  const [savingCat, setSavingCat] = useState(false);
  const [savingTag, setSavingTag] = useState(false);
  // Confirm-delete a category/tag from the catalog.
  const [delTaxon, setDelTaxon] = useState<{ kind: 'category' | 'tag'; id: string; name: string } | null>(null);
  const [deletingTaxon, setDeletingTaxon] = useState(false);
  // Add-field modal (creates a field on this item type).
  const [addFieldOpen, setAddFieldOpen] = useState(false);
  const [newFieldName, setNewFieldName] = useState('');
  const [newFieldType, setNewFieldType] = useState('TEXT');
  const [newFieldRequired, setNewFieldRequired] = useState(false);
  const [savingField, setSavingField] = useState(false);
  // Confirm-delete a field (from the type), confirm-clear media, rename taxon.
  const [delField, setDelField] = useState<string | null>(null);
  const [deletingField, setDeletingField] = useState(false);
  // Inline "add option" for ENUM/MULTI_SELECT fields.
  const [addOptionFor, setAddOptionFor] = useState<string | null>(null);
  const [optionValue, setOptionValue] = useState('');
  const [savingOption, setSavingOption] = useState(false);
  const [delOption, setDelOption] = useState<{ fieldKey: string; opt: string } | null>(null);
  const [removingOption, setRemovingOption] = useState(false);
  const [clearMediaKey, setClearMediaKey] = useState<string | null>(null);
  const [editTaxon, setEditTaxon] = useState<{ kind: 'category' | 'tag'; id: string; name: string } | null>(null);
  const [editTaxonName, setEditTaxonName] = useState('');
  const [savingTaxonEdit, setSavingTaxonEdit] = useState(false);
  // Manage a collection's items — opened by tapping a collection chip in the
  // Collections card. Same "Add items" card as the standalone Collections screen.
  const [manageColl, setManageColl] = useState<Collection | null>(null);
  const [manageCollItems, setManageCollItems] = useState<any[]>([]);
  const [collRename, setCollRename] = useState('');
  const [collAllItems, setCollAllItems] = useState<CatalogItem[]>([]);
  // Bundle components (this item as a bundle parent). Mirrors the standalone
  // Bundles screen: pick a component item + relation type + quantity, adjust
  // quantity inline, and relink the rollup price.
  const [components, setComponents] = useState<any[]>([]);
  const [pickItemId, setPickItemId] = useState('');
  const [bundleRelation, setBundleRelation] = useState<RelationType>('BUNDLE_DYNAMIC');
  const [bundleQty, setBundleQty] = useState('1');
  const [addCompOpen, setAddCompOpen] = useState(false);
  const [addingComponent, setAddingComponent] = useState(false);
  const [pickerItems, setPickerItems] = useState<{ value: string; label: string }[]>([]);
  const [delComponent, setDelComponent] = useState<{ childItemId: string; label: string } | null>(null);
  const [removingComponent, setRemovingComponent] = useState(false);
  // Edit an existing component (relation + quantity).
  const [editComp, setEditComp] = useState<any | null>(null);
  const [editCompRelation, setEditCompRelation] = useState<RelationType>('BUNDLE_DYNAMIC');
  const [editCompQty, setEditCompQty] = useState('1');
  const [savingEditComp, setSavingEditComp] = useState(false);
  // Component prices (id → base_price) for the rollup, + manual bundle price.
  const [compPrices, setCompPrices] = useState<Record<string, number | null>>({});
  // This item's own current base_price (the bundle's price).
  const [currentPrice, setCurrentPrice] = useState<number | null>(null);

  // Variants / resolve / suggestions
  const [variants, setVariants] = useState<any[] | null>(null);
  const [resolved, setResolved] = useState<any | null>(null);
  const [suggestions, setSuggestions] = useState<Suggestion[]>([]);

  const loadFields = useCallback(async (typeId: string) => {
    try {
      const [fieldsRes, typeRes] = await Promise.all([
        getItemTypeFields(typeId),
        getItemType(typeId).catch(() => null),
      ]);
      setFields(fieldsRes.items ?? []);
      if (typeRes?.name) setTypeName(typeRes.name);
    } catch (err) {
      handleApiError(err, 'Could not load field definitions');
    }
  }, []);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      if (isNew) {
        if (params.type) await loadFields(params.type);
        setVals({});
        setState('DRAFT');
      } else {
        // Not new → an item id is always present in the route params.
        const it = await getItem(params.id!);
        setItem(it);
        setSku(it.skuOrReference || '');
        setState(it.currentState || 'DRAFT');
        // CC-002 CR-1. Reload clears any stale publish block — the operator may
        // have just fixed the code, and a block that outlives its cause reads as
        // the UI being broken.
        setHsnSac(it.hsnSac || '');
        setHsnBlocked(false);
        // fieldValues: [{ fieldDefinitionId, valueJson, fieldDefinition:{fieldKey}, locationId? }]
        // → flat { fieldKey: value } map (global values only).
        const map: Record<string, any> = {};
        const fvs = (it as any).fieldValues || [];
        for (const fv of fvs) {
          if (fv.locationId) continue;
          const key = fv.fieldDefinition?.fieldKey ?? fv.fieldKey;
          if (key) map[key] = fv.valueJson ?? fv.value;
        }
        setVals(map);
        // Pre-select the taxonomy this item already has, so the chips reflect
        // what's saved (categories by id; tags by name → id is resolved once
        // the tag list loads, via the effect below).
        const cats = (it as any).itemCategories || [];
        setSelCats(new Set(cats.map((c: any) => c.categoryId)));
        const itTags = (it as any).itemTags || [];
        setAssignedTagNames(itTags.map((t: any) => t.tag?.name).filter(Boolean));
        if (it.itemTypeId) await loadFields(it.itemTypeId);
      }
      setDirty(false);
    } catch (err) {
      handleApiError(err, 'Could not load item');
    } finally {
      setLoading(false);
    }
  }, [isNew, params.id, params.type, loadFields]);

  useReloadOnFocus(load);

  const setField = (key: string, v: any) => { setVals(p => ({ ...p, [key]: v })); setDirty(true); };

  /* ── CC-002 CR-1 · taxability, read the way the API reads it ──────────────
     From the item's own field values, not a column — see utils/taxability.ts.
     EXEMPT items don't show the requirement at all (DB-001 §1); UNKNOWN shows
     the field but does not assert it is required, because the server-side
     default decides and the console does not get to guess. */
  const taxability = taxabilityOf(vals);
  const showHsnCard = taxability !== 'EXEMPT';
  const hsnRequired = taxability === 'TAXABLE';
  const hsnMissing = !hasHsnSac(hsnSac);

  /* ── CC-002 CR-7 · only offer transitions that are legal ─────────────────
     DB-001 §4: "Illegal transitions aren't offered as options rather than
     erroring after the fact." ARCHIVED is terminal, so an archived item offers
     nothing. */
  const legalNext = allowedTransitions(state as any);
  const canPublish = legalNext.includes('PUBLISHED');
  const canDeactivateNow = legalNext.includes('DEACTIVATED');

  /* ── CC-002 CR-6 · sellability, stated plainly ───────────────────────────── */
  const isSellable = isSellableState(state);
  const notSellableNote = notSellableReason(state);

  const buildFieldsPayload = () =>
    fields
      .filter(f => vals[f.fieldKey] !== undefined && vals[f.fieldKey] !== '')
      .map(f => ({
        fieldKey: f.fieldKey,
        value: f.fieldType === 'NUMBER' ? Number(vals[f.fieldKey]) : vals[f.fieldKey],
      }));

  // Open the review/confirm dialog (no-op if a save is already in flight).
  const requestSave = () => { if (!busy) setConfirmSaveOpen(true); };

  const save = async () => {
    setConfirmSaveOpen(false);
    setBusy(true);
    try {
      if (isNew) {
        const created = await createItem({
          // New-item entry points always pass the item type in route params.
          itemTypeId: params.type!,
          currentState: 'DRAFT',
          ...(sku ? { skuOrReference: sku } : {}),
          fields: buildFieldsPayload(),
        });
        showSuccess(created, 'Item created', 'Saved as draft.');
        // A new item consumed one unit of the plan's item quota — refresh so
        // the dashboard bar + create gating reflect it.
        void refreshQuota();
        navigation.navigate('editor', { id: created.id });
      } else {
        const updated = await updateItem(item!.id, {
          ...(sku ? { skuOrReference: sku } : {}),
          // CC-002 CR-1 — `null` clears the code (an item reclassified exempt).
          hsnSac: hasHsnSac(hsnSac) ? hsnSac.trim() : null,
          fields: buildFieldsPayload(),
        });
        showSuccess(updated, 'Changes saved', 'Item updated.');
        await load();
      }
    } catch (err) {
      // CC-002 CR-2. A brand-locked field refused at location scope is not a
      // generic save failure — name the field, so the operator knows which of
      // the values they just edited is not theirs to change.
      if (isFieldLockedError(err)) {
        const key = getLockedFieldKey(err);
        toast.error(key ? `“${key}” is locked by the brand.` : 'That field is locked by the brand.');
      } else {
        handleApiError(err, 'Could not save item');
      }
      // If the create was blocked at the plan cap, refresh usage so the UI
      // gates further attempts (the backend already returned the reason).
      void refreshQuota();
    } finally {
      setBusy(false);
    }
  };

  /* ── CC-002 CR-1 + CR-7 · Publish ─────────────────────────────────────────
     The editor is where a refused publish has to surface, because it is the
     only screen with the HSN/SAC input on it. The Items screen's bulk publish
     can only say "3 failed"; here we can say exactly what is missing, next to
     the box to fix it.

     Two refusals are handled specifically rather than through the generic toast:
       422 hsn_sac_required   → the inline block (DB-001 §1)
       409 illegal_transition → the state moved under us (a stale list, a
                                concurrent edit). Re-read rather than argue.
  */
  const doPublish = async () => {
    // Pre-flight, so a taxable item with no code never even sends the request —
    // the operator gets the block immediately instead of a round trip.
    if (hsnRequired && hsnMissing) {
      setHsnBlocked(true);
      return;
    }
    setBusy(true);
    try {
      const res = await publishItem(item!.id);
      setHsnBlocked(false);
      showSuccess(res, 'Published', 'Item is live in the catalog.');
      setState('PUBLISHED');
      await load();
    } catch (err) {
      if (isHsnSacRequiredError(err)) {
        // The server knows the item's taxability better than the console does
        // (it resolves it through the inheritance chain), so its refusal wins
        // even when the local check thought the item was exempt.
        setHsnBlocked(true);
      } else if (isIllegalTransitionError(err)) {
        const meta = getIllegalTransitionMeta(err);
        toast.error(
          meta
            ? `Can't publish from ${meta.from.toLowerCase()}.`
            : 'That change is not allowed from this state.',
        );
        await load();
      } else {
        handleApiError(err, 'Could not publish');
      }
    } finally {
      setBusy(false);
    }
  };

  /* ── CC-002 CR-5 · price history (CAT-D2) ────────────────────────────────
     Loaded lazily, behind the quiet "history" affordance DB-001 §3 asks for —
     the current price is the thing the operator came for, and the ladder can be
     long on an item that has been re-priced for years. */
  const loadPriceHistory = async () => {
    if (!item?.id) return;
    setLoadingPriceHistory(true);
    try {
      const res = await getBasePriceHistory(item.id);
      setPriceHistory(res.items ?? []);
      setPriceHistoryOpen(true);
    } catch (err) {
      handleApiError(err, 'Could not load price history');
    } finally {
      setLoadingPriceHistory(false);
    }
  };

  /* ── CC-002 CR-2 · lock / unlock a field for the whole brand (P02) ────────
     A brand standard, so it is a property of the field DEFINITION (the type),
     not of this item — locking here locks the field everywhere. The confirm
     copy says so.

     Price fields never reach here: `isLockableField` keeps the toggle off them
     entirely (DB-001 §2). The 400 handler exists for the case where the type
     was retyped to CURRENCY in another tab. */
  const toggleFieldLock = async (fieldKey: string, next: boolean) => {
    const typeId = item?.itemTypeId ?? params.type;
    if (!typeId) return;
    setLockingField(fieldKey);
    try {
      await updateItemTypeField(typeId, fieldKey, { isLocked: next } as any);
      toast.success(
        next
          ? `“${fieldKey}” is now set by the brand — locations can't override it.`
          : `“${fieldKey}” unlocked — locations can override it again.`,
      );
      await loadFields(typeId);
    } catch (err) {
      if (isPriceFieldNotLockableError(err)) {
        toast.error('Price fields stay locally flexible and can\'t be locked.');
      } else {
        handleApiError(err, 'Could not change the field lock');
      }
    } finally {
      setLockingField(null);
    }
  };

  const doDeactivate = async () => {
    setBusy(true);
    try {
      const res = await deactivateItem(item!.id);
      showSuccess(res, 'Deactivated', 'Item removed from the live catalog.');
      setDeactOpen(false);
      setState('DEACTIVATED');
      await load();
    } catch (err) {
      handleApiError(err, 'Could not deactivate');
    } finally {
      setBusy(false);
    }
  };

  const loadVersions = async () => {
    try {
      const res = await getItemVersions(item!.id);
      setVersions(res.items ?? res.versions ?? (Array.isArray(res) ? res : []));
    } catch (err) {
      handleApiError(err, 'Could not load versions');
    }
  };

  // Load tenant taxonomy + this item's suggestions once the item is known.
  useEffect(() => {
    // Load tenant-wide categories/tags even for a new item so the chips show
    // (assignment still needs the item to be saved first — buttons gate that).
    (async () => {
      try {
        const [cats, tags] = await Promise.all([getCategories(), getTags()]);
        setAllCategories(cats.items ?? []);
        setAllTags(tags.items ?? []);
      } catch { /* non-fatal */ }
    })();
  }, []);

  // Resolve the item's assigned tag names → ids once both lists are available,
  // so the saved tags show highlighted in the chip row.
  useEffect(() => {
    if (!assignedTagNames.length || !allTags.length) return;
    const wanted = new Set(assignedTagNames);
    setSelTags(new Set(allTags.filter(t => wanted.has(t.name)).map(t => t.id)));
  }, [assignedTagNames, allTags]);

  const loadSuggestions = useCallback(async () => {
    if (isNew || !item) return;
    try {
      // Only this item's still-pending suggestions — accepted/rejected ones are
      // done and must not show an Accept/Reject action (that would 400).
      const res = await getSuggestions({ catalogItemId: item.id, status: 'PENDING' });
      setSuggestions((res as any).items ?? []);
    } catch (err) {
      handleApiError(err, 'Could not load suggestions');
    }
  }, [isNew, item]);

  useEffect(() => { loadSuggestions(); }, [loadSuggestions]);

  const saveTaxonomy = async () => {
    if (!item) return;
    setSavingTax(true);
    try {
      await setItemCategories(item.id, { categoryIds: Array.from(selCats) });
      await setItemTags(item.id, { tagNames: allTags.filter(t => selTags.has(t.id)).map(t => t.name) });
      showSuccess({}, 'Taxonomy updated', 'Categories & tags assigned to this item.');
    } catch (err) {
      handleApiError(err, 'Could not update taxonomy');
    } finally {
      setSavingTax(false);
    }
  };

  const loadVariants = async () => {
    try {
      const res = await getItemVariants(item!.id);
      setVariants((res as any).items ?? (Array.isArray(res) ? res : []));
    } catch (err) {
      handleApiError(err, 'Could not load variants');
    }
  };

  const doResolve = async () => {
    try {
      const res = await resolveItem(item!.id);
      setResolved(res);
      showSuccess({}, 'Resolved', 'Customer-facing view computed.');
    } catch (err) {
      handleApiError(err, 'Could not resolve item');
    }
  };

  const doExport = async () => {
    try {
      const res = await exportItem(item!.id);
      // Browser download of the export JSON.
      if (Platform.OS === 'web') {
        const blob = new Blob([JSON.stringify(res, null, 2)], { type: 'application/json' });
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url; a.download = `${item!.skuOrReference || item!.id}.json`; a.click();
        URL.revokeObjectURL(url);
      }
      showSuccess({}, 'Exported', 'Item export downloaded.');
    } catch (err) {
      handleApiError(err, 'Could not export item');
    }
  };

  const onAcceptSuggestion = async (s: Suggestion) => {
    try {
      await acceptSuggestion(s.id);
      showSuccess({}, 'Suggestion accepted', 'Applied to the item.');
      await Promise.all([loadSuggestions(), load()]);
    } catch (err) {
      handleApiError(err, 'Could not accept suggestion');
    }
  };

  const onRejectSuggestion = async (s: Suggestion) => {
    try {
      await rejectSuggestion(s.id);
      showSuccess({}, 'Suggestion rejected', 'Dismissed.');
      await loadSuggestions();
    } catch (err) {
      handleApiError(err, 'Could not reject suggestion');
    }
  };

  const toggleSet = (setter: React.Dispatch<React.SetStateAction<Set<string>>>, id: string) =>
    setter(prev => { const n = new Set(prev); n.has(id) ? n.delete(id) : n.add(id); return n; });

  // Create a new category / tag inline, then auto-select it.
  const addCategory = async () => {
    const name = catName.trim();
    if (!name) return;
    const isSub = !!catParentId;
    setSavingCat(true);
    try {
      const c = await createCategory({ name, ...(catParentId ? { parentCategoryId: catParentId } : {}) });
      setAllCategories(prev => [...prev, c]);
      setSelCats(prev => new Set(prev).add(c.id));
      setCatName('');
      setCatParentId('');
      setAddingCat(false);
      toast.success(isSub ? 'Sub-category added' : 'Category added');
    } catch (err) {
      handleApiError(err, 'Could not add category');
    } finally {
      setSavingCat(false);
    }
  };
  const addTag = async () => {
    const name = tagName.trim();
    if (!name) return;
    setSavingTag(true);
    try {
      const t = await createTag({ name });
      setAllTags(prev => [...prev, t]);
      setSelTags(prev => new Set(prev).add(t.id));
      setTagName('');
      setAddingTag(false);
      toast.success('Tag added');
    } catch (err) {
      handleApiError(err, 'Could not add tag');
    } finally {
      setSavingTag(false);
    }
  };

  // Delete a category/tag from the catalog (after confirm), then drop it from
  // the lists + any selection.
  const confirmDeleteTaxon = async () => {
    if (!delTaxon) return;
    setDeletingTaxon(true);
    try {
      if (delTaxon.kind === 'category') {
        await deleteCategory(delTaxon.id);
        setAllCategories(prev => prev.filter(c => c.id !== delTaxon.id));
        setSelCats(prev => { const n = new Set(prev); n.delete(delTaxon.id); return n; });
      } else {
        await deleteTag(delTaxon.id);
        setAllTags(prev => prev.filter(t => t.id !== delTaxon.id));
        setSelTags(prev => { const n = new Set(prev); n.delete(delTaxon.id); return n; });
      }
      toast.success(`${delTaxon.kind === 'category' ? 'Category' : 'Tag'} deleted`);
      setDelTaxon(null);
    } catch (err) {
      handleApiError(err, 'Could not delete');
    } finally {
      setDeletingTaxon(false);
    }
  };

  // Create a new field on this item type from the editor.
  const addNewField = async () => {
    const label = newFieldName.trim();
    if (!label) return;
    const typeId = item?.itemTypeId || params.type;
    if (!typeId) { toast.error('Save the item first'); return; }
    const fieldKey = label.toLowerCase().replace(/[^a-z0-9]+/g, '_').replace(/^_+|_+$/g, '') || 'field';
    setSavingField(true);
    try {
      await addItemTypeField(typeId, { fieldKey, fieldType: newFieldType as any, isRequired: newFieldRequired });
      await loadFields(typeId);
      toast.success('Field added');
      setAddFieldOpen(false);
      setNewFieldName('');
      setNewFieldType('TEXT');
      setNewFieldRequired(false);
    } catch (err) {
      handleApiError(err, 'Could not add field');
    } finally {
      setSavingField(false);
    }
  };

  // Delete a field from the item type (after confirm), then refresh.
  const confirmDeleteField = async () => {
    if (!delField) return;
    const typeId = item?.itemTypeId || params.type;
    if (!typeId) return;
    setDeletingField(true);
    try {
      await deleteItemTypeField(typeId, delField);
      await loadFields(typeId);
      toast.success('Field deleted');
      setDelField(null);
    } catch (err) {
      handleApiError(err, 'Could not delete field');
    } finally {
      setDeletingField(false);
    }
  };

  // Add a dropdown option to an ENUM/MULTI_SELECT field (persists to the type
  // config), then reload so the new value appears in the dropdown.
  const addFieldOption = async () => {
    if (!addOptionFor) return;
    const v = optionValue.trim();
    if (!v) return;
    const typeId = item?.itemTypeId || params.type;
    if (!typeId) return;
    const f = fields.find(x => x.fieldKey === addOptionFor);
    const existing = f?.config?.options ?? [];
    if (existing.includes(v)) { toast.info('Option already exists'); return; }
    setSavingOption(true);
    try {
      await updateItemTypeField(typeId, addOptionFor, {
        config: { options: [...existing, v], ...(f?.config?.unit ? { unit: f.config.unit } : {}) },
      });
      await loadFields(typeId);
      toast.success('Option added');
      setAddOptionFor(null);
      setOptionValue('');
    } catch (err) {
      handleApiError(err, 'Could not add option');
    } finally {
      setSavingOption(false);
    }
  };

  // Remove a single dropdown option from an ENUM/MULTI_SELECT field (after confirm).
  const confirmRemoveOption = async () => {
    if (!delOption) return;
    const { fieldKey, opt } = delOption;
    const typeId = item?.itemTypeId || params.type;
    if (!typeId) return;
    const f = fields.find(x => x.fieldKey === fieldKey);
    const next = (f?.config?.options ?? []).filter(o => o !== opt);
    setRemovingOption(true);
    try {
      await updateItemTypeField(typeId, fieldKey, {
        config: { ...(next.length ? { options: next } : {}), ...(f?.config?.unit ? { unit: f.config.unit } : {}) },
      });
      // Drop the removed value from this item's value too.
      setVals(prev => {
        const v = prev[fieldKey];
        if (Array.isArray(v)) return { ...prev, [fieldKey]: v.filter((x: string) => x !== opt) };
        if (v === opt) return { ...prev, [fieldKey]: '' };
        return prev;
      });
      await loadFields(typeId);
      toast.success('Option removed');
      setDelOption(null);
    } catch (err) {
      handleApiError(err, 'Could not remove option');
    } finally {
      setRemovingOption(false);
    }
  };

  // Rename a category/tag (the edit action), then update lists.
  const saveTaxonEdit = async () => {
    if (!editTaxon) return;
    const newName = editTaxonName.trim();
    if (!newName) return;
    setSavingTaxonEdit(true);
    try {
      if (editTaxon.kind === 'category') {
        await updateCategory(editTaxon.id, { name: newName });
        setAllCategories(prev => prev.map(c => (c.id === editTaxon.id ? { ...c, name: newName } : c)));
      } else {
        await updateTag(editTaxon.id, { name: newName });
        setAllTags(prev => prev.map(t => (t.id === editTaxon.id ? { ...t, name: newName } : t)));
      }
      toast.success('Renamed');
      setEditTaxon(null);
    } catch (err) {
      handleApiError(err, 'Could not rename');
    } finally {
      setSavingTaxonEdit(false);
    }
  };

  // Resolve base_price for the given component ids (via getItem) → compPrices.
  const loadCompPrices = useCallback(async (comps: any[]) => {
    const ids = Array.from(new Set(comps.map((c: any) => c.childItemId).filter(Boolean)));
    if (!ids.length) return;
    const entries = await Promise.all(ids.map(async (id) => {
      try { return [id, extractPrice(await getItem(id))] as const; }
      catch { return [id, null] as const; }
    }));
    setCompPrices(prev => { const next = { ...prev }; for (const [id, p] of entries) next[id] = p; return next; });
  }, []);

  // Load this item's bundle components + the item picker (other items).
  useEffect(() => {
    if (!item?.id) return;
    getComponents(item.id).then(r => { const comps = r.items ?? []; setComponents(comps); loadCompPrices(comps); }).catch(() => {});
    getItems()
      .then(r => setPickerItems((r.items ?? [])
        // Only PUBLISHED items can be bundle components (no drafts/deactivated/archived).
        .filter((i: any) => i.id !== item.id && i.currentState === 'PUBLISHED')
        .map((i: any) => ({ value: i.id, label: i.skuOrReference || (i.id || '').slice(0, 8) }))))
      .catch(() => {});
  }, [item?.id, loadCompPrices]);

  // Keep the bundle's displayed price in sync with the loaded item.
  useEffect(() => { setCurrentPrice(extractPrice(item)); }, [item]);

  /* CC-002 CR-5 — read the dated ledger's OPEN row for the price card. Separate
     from `currentPrice` (which the bundle rollup uses as a major-unit number):
     this is the authoritative, currency-carrying value, and it is what gets
     displayed. Closing the history panel on item change stops a stale ladder
     from another item lingering. */
  useEffect(() => {
    if (!item?.id) { setBasePrice(null); return; }
    let cancelled = false;
    (async () => {
      try {
        const res = await getBasePrice(item.id);
        if (!cancelled) setBasePrice(res);
      } catch {
        // Non-fatal: an item whose type has no price field simply has no ledger
        // row, and the card falls back to the field value.
        if (!cancelled) setBasePrice(null);
      }
    })();
    setPriceHistory(null);
    setPriceHistoryOpen(false);
    return () => { cancelled = true; };
  }, [item?.id]);

  // Auto-recompute the rollup after a component change, but only for bundles
  // with a dynamic component (so a manually-priced static bundle isn't touched).
  const autoRelinkIfDynamic = async (comps: any[]) => {
    if (!item?.id) return;
    if (!comps.some((c: any) => c.relationType === 'BUNDLE_DYNAMIC')) return;
    try {
      await relinkPrice(item.id, { mode: 'recompute' });
      setCurrentPrice(extractPrice(await getItem(item.id)));
    } catch { /* non-fatal */ }
  };

  const addBundleComponent = async () => {
    if (!item?.id || !pickItemId) return;
    setAddingComponent(true);
    try {
      await addComponent(item.id, { childItemId: pickItemId, relationType: bundleRelation, quantity: Number(bundleQty) || 1 });
      const r = await getComponents(item.id);
      setComponents(r.items ?? []);
      loadCompPrices(r.items ?? []);
      await autoRelinkIfDynamic(r.items ?? []);
      setPickItemId('');
      setBundleRelation('BUNDLE_DYNAMIC');
      setBundleQty('1');
      setAddCompOpen(false);
      toast.success('Component added');
    } catch (err) {
      handleApiError(err, 'Could not add component');
    } finally {
      setAddingComponent(false);
    }
  };

  // Adjust a component's quantity (min 1) and persist, then refresh.
  const changeComponentQty = async (c: any, delta: number) => {
    if (!item?.id) return;
    // quantity may arrive as a string — coerce before arithmetic so `"5" + (-1)`
    // doesn't string-concat into NaN (which serializes to null and 400s).
    const current = Number(c.quantity) || 1;
    const next = Math.max(1, current + delta);
    if (next === current) return;
    try {
      await updateComponent(item.id, c.childItemId, { quantity: next });
      const r = await getComponents(item.id);
      setComponents(r.items ?? []);
      loadCompPrices(r.items ?? []);
      await autoRelinkIfDynamic(r.items ?? []);
    } catch (err) {
      handleApiError(err, 'Could not update component');
    }
  };

  const openEditComponent = (c: any) => {
    setEditComp(c);
    setEditCompRelation(c.relationType);
    setEditCompQty(String(Number(c.quantity) || 1));
  };

  const saveEditComponent = async () => {
    if (!item?.id || !editComp) return;
    setSavingEditComp(true);
    try {
      await updateComponent(item.id, editComp.childItemId, {
        relationType: editCompRelation,
        quantity: Number(editCompQty) || 1,
      });
      const r = await getComponents(item.id);
      setComponents(r.items ?? []);
      loadCompPrices(r.items ?? []);
      await autoRelinkIfDynamic(r.items ?? []);
      setEditComp(null);
      toast.success('Component updated');
    } catch (err) {
      handleApiError(err, 'Could not update component');
    } finally {
      setSavingEditComp(false);
    }
  };


  const confirmRemoveComponent = async () => {
    if (!item?.id || !delComponent) return;
    setRemovingComponent(true);
    try {
      await removeComponent(item.id, delComponent.childItemId);
      const remaining = components.filter(c => c.childItemId !== delComponent.childItemId);
      setComponents(remaining);
      await autoRelinkIfDynamic(remaining);
      toast.success('Component removed');
      setDelComponent(null);
    } catch (err) {
      handleApiError(err, 'Could not remove component');
    } finally {
      setRemovingComponent(false);
    }
  };

  const labelForItem = (id: string) => pickerItems.find(p => p.value === id)?.label || id.slice(0, 8);

  // ── Collection member management (opened from the Collections card) ──
  // Label for any item id, drawn from the full item list loaded for this modal.
  const collItemLabel = (id?: string) => {
    const it: any = collAllItems.find(x => x.id === id);
    const nm = it?.fieldValues?.[0]?.valueJson;
    const name = typeof nm === 'string' ? nm : undefined;
    return name || it?.skuOrReference || (id || '').slice(0, 8);
  };

  // Open the manage card for a collection — load its members + all items (for
  // the "Add items" picker), in parallel.
  const openCollManage = useCallback(async (c: Collection) => {
    setManageColl(c); setCollRename(c.name);
    try {
      const [detail, all] = await Promise.all([getCollection(c.id), getItems({ limit: 200 })]);
      setManageCollItems((detail as any).items ?? (detail as any).collectionItems ?? []);
      setCollAllItems((all.items ?? []).filter((it: any) => it.currentState !== 'ARCHIVED'));
    } catch (err) { handleApiError(err, 'Could not open collection'); }
  }, []);

  const refreshCollManage = useCallback(async () => {
    if (!manageColl) return;
    try {
      const detail = await getCollection(manageColl.id);
      setManageCollItems((detail as any).items ?? (detail as any).collectionItems ?? []);
    } catch { /* ignore */ }
  }, [manageColl]);

  const addItemToColl = useCallback(async (itemId: string) => {
    if (!manageColl) return;
    try {
      await addCollectionItems(manageColl.id, { items: [{ catalogItemId: itemId }] });
      showSuccess({}, 'Item added', 'Added to collection.');
      await refreshCollManage();
    } catch (err) { handleApiError(err, 'Could not add item'); }
  }, [manageColl, refreshCollManage]);

  const removeItemFromColl = useCallback(async (itemId: string) => {
    if (!manageColl) return;
    try {
      await removeCollectionItem(manageColl.id, itemId);
      showSuccess({}, 'Item removed', 'Removed from collection.');
      await refreshCollManage();
    } catch (err) { handleApiError(err, 'Could not remove item'); }
  }, [manageColl, refreshCollManage]);

  const renameColl = useCallback(async () => {
    if (!manageColl || !collRename.trim()) return;
    try {
      await updateCollection(manageColl.id, { name: collRename.trim() });
      showSuccess({}, 'Renamed', 'Collection updated.');
      setManageColl(null);
    } catch (err) { handleApiError(err, 'Could not rename'); }
  }, [manageColl, collRename]);

  // Move a member up (-1) or down (+1): swap locally, then persist distinct
  // sortOrder = new index for every member so the backend keeps the order.
  const moveCollMember = useCallback(async (itemId: string, dir: -1 | 1) => {
    if (!manageColl) return;
    const idx = manageCollItems.findIndex((m: any) => (m.catalogItemId ?? m.id) === itemId);
    const j = idx + dir;
    if (idx < 0 || j < 0 || j >= manageCollItems.length) return;
    const next = [...manageCollItems];
    [next[idx], next[j]] = [next[j], next[idx]];
    setManageCollItems(next); // optimistic
    try {
      await reorderCollectionItems(
        manageColl.id,
        next.map((m: any, i: number) => ({ catalogItemId: m.catalogItemId ?? m.id, sortOrder: i })),
      );
    } catch (err) {
      handleApiError(err, 'Could not reorder item');
      refreshCollManage();
    }
  }, [manageColl, manageCollItems, refreshCollManage]);

  const collMemberIds = new Set(manageCollItems.map((m: any) => m.catalogItemId ?? m.id));

  // ── Media (field-scoped async upload pipeline, web file picker) ──
  const [mediaStatus, setMediaStatus] = useState<Record<string, any>>({});
  const mediaStatusRef = useRef<Record<string, any>>({});
  useEffect(() => { mediaStatusRef.current = mediaStatus; }, [mediaStatus]);
  // Media fields hidden after a confirmed Clear (until the screen reloads).
  const [clearedMedia, setClearedMedia] = useState<string[]>([]);

  const refreshMedia = useCallback(async (fieldKey: string) => {
    try {
      const res = await getMediaStatus(item!.id, fieldKey);
      setMediaStatus(p => ({ ...p, [fieldKey]: (res as any).data ?? res }));
    } catch { /* no media yet */ }
  }, [item]);

  const uploadMedia = async (fieldKey: string) => {
    if (Platform.OS !== 'web') { toast.info('Use the web app to upload media.'); return; }
    const input = document.createElement('input');
    input.type = 'file';
    input.onchange = async () => {
      const file = input.files?.[0];
      if (!file) return;
      try {
        // 1) initiate → presigned PUT
        const init: any = await initiateUpload(item!.id, fieldKey, {
          contentType: file.type || 'application/octet-stream',
          sizeBytes: file.size,
          originalFilename: file.name,
        });
        const data = init.data ?? init;
        const url = data.presigned_url ?? data.presignedUrl ?? data.url;
        // 2) PUT bytes to the presigned URL — bare axios (no shared instance):
        //    withCredentials:false keeps our auth cookies off the storage host,
        //    and transformRequest passes the raw File through untouched so the
        //    signed Content-Type matches and the upload isn't re-encoded.
        if (url) {
          await axios.request({
            url,
            method: (data.method || 'PUT'),
            data: file,
            headers: data.headers || { 'Content-Type': file.type },
            withCredentials: false,
            transformRequest: [(body) => body],
          });
        }
        // Show an instant local preview so the user sees their image even
        // while the asset is still PROCESSING (before a cdn url exists).
        const localUrl = Platform.OS === 'web' ? URL.createObjectURL(file) : undefined;
        setMediaStatus(p => ({ ...p, [fieldKey]: { ...(p[fieldKey] || {}), localUrl } }));
        // 3) complete → PROCESSING/READY
        await completeUpload(item!.id, fieldKey);
        showSuccess({}, 'Upload complete', 'Media attached to the field.');
        await refreshMedia(fieldKey);
        // Poll a few times so PROCESSING → READY (cdn url) updates without a
        // manual "Status" click. (Requires the backend outbox worker running.)
        let tries = 0;
        const poll = setInterval(async () => {
          tries++;
          await refreshMedia(fieldKey);
          const st = (mediaStatusRef.current || {})[fieldKey];
          if (tries >= 6 || st?.status === 'READY' || st?.cdnUrl) clearInterval(poll);
        }, 2500);
      } catch (err) {
        handleApiError(err, 'Media upload failed');
      }
    };
    input.click();
  };

  const clearMedia = async (fieldKey: string) => {
    try {
      await clearUpload(item!.id, fieldKey);
      showSuccess({}, 'Media cleared', 'Field reset.');
      setMediaStatus(p => ({ ...p, [fieldKey]: null }));
      // Hide this media row from the screen after clearing.
      setClearedMedia(prev => (prev.includes(fieldKey) ? prev : [...prev, fieldKey]));
    } catch (err) {
      handleApiError(err, 'Could not clear media');
    }
  };

  // ── Price what-if (GET component-price-impact) + commit (POST /items/:id/price) ──
  const [priceValue, setPriceValue] = useState('');
  const [impact, setImpact] = useState<any | null>(null);

  // Price propagation needs a `base_price` field on the type. If the type
  // doesn't have one yet, create it automatically (CURRENCY) so the feature
  // works out of the box — no manual schema editing required.
  const ensureBasePriceField = async (): Promise<boolean> => {
    if (fields.some(f => f.fieldKey === 'base_price')) return true;
    const typeId = item?.itemTypeId || params.type;
    if (!typeId) return false;
    try {
      await addItemTypeField(typeId, { fieldKey: 'base_price', fieldType: 'CURRENCY' });
      await loadFields(typeId); // refresh so the new field shows in the editor
      return true;
    } catch (err) {
      handleApiError(err, 'Could not enable the price field');
      return false;
    }
  };

  const previewPrice = async () => {
    const value = Number(priceValue);
    if (!priceValue || Number.isNaN(value)) { toast.error('Enter a numeric price to preview impact.'); return; }
    if (!(await ensureBasePriceField())) return;
    try {
      const res = await getPriceImpact(item!.id, { value });
      setImpact(res);
      showSuccess({}, 'Impact computed', 'Preview of how this price ripples to bundles.');
    } catch (err) {
      handleApiError(err, 'Could not compute price impact');
    }
  };

  const commitNewPrice = async () => {
    const value = Number(priceValue);
    if (!priceValue || Number.isNaN(value)) { toast.error('Enter a numeric price first.'); return; }
    if (!(await ensureBasePriceField())) return;
    try {
      await commitPrice(item!.id, { value });
      showSuccess({}, 'Price committed', 'Applied + propagated to ancestor bundles.');
      setImpact(null);
      await load();
    } catch (err) {
      handleApiError(err, 'Could not commit price');
    }
  };

  // Turn a structured suggestion into a human-readable sentence for the card.
  const suggestionMessage = (s: any): string => {
    const p = s?.payload ?? {};
    switch (s?.kind) {
      case 'TAG':   return `Add the tag “${p.name ?? ''}” to this item`;
      case 'FIELD': return `Set “${p.fieldKey ?? 'a field'}” to “${typeof p.value === 'object' ? JSON.stringify(p.value) : p.value ?? ''}”`;
      case 'CATEGORY': return `Add this item to a category${p.name ? ` (“${p.name}”)` : ''}`;
      default:      return JSON.stringify(p);
    }
  };

  // ── Generate suggestions with real AI (Claude) ──
  // POST /items/:id/suggestions/generate — the backend reads this item and asks
  // Claude to propose tags/field values, persisted as PENDING for review.
  const [generating, setGenerating] = useState(false);
  const runAiSuggestions = async () => {
    if (!item) return;
    setGenerating(true);
    try {
      const res = await generateSuggestions(item.id);
      if (res.created > 0) {
        showSuccess({}, 'AI suggestions ready', `Claude proposed ${res.created} suggestion${res.created > 1 ? 's' : ''} for review.`);
      } else {
        showSuccess({}, 'No new suggestions', 'Claude had nothing new to add for this item right now.');
      }
      await loadSuggestions();
    } catch (err) {
      handleApiError(err, 'Could not generate AI suggestions');
    } finally {
      setGenerating(false);
    }
  };

  // ── styles ──
  const sectionCard: ViewStyle = {
    backgroundColor: colors.card, borderWidth: 1, borderColor: colors.line,
    borderRadius: radii.r3, marginBottom: 16,
  };
  const sectionHead: ViewStyle = {
    paddingVertical: 13, paddingHorizontal: 16, borderBottomWidth: 1, borderBottomColor: colors.line,
    flexDirection: 'row', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 8, rowGap: 10,
  };
  const inputStyle = {
    borderWidth: 1, borderColor: colors.line, borderRadius: radii.r2, paddingHorizontal: 12,
    paddingVertical: 10, fontSize: fontSize.md, color: colors.ink, fontFamily: font.sans,
    backgroundColor: colors.card,
    ...(Platform.OS === 'web' ? { outlineStyle: 'none' as any } : {}),
  };

  // Editor buttons inherit the app-wide adaptive primary: the Button itself
  // resolves the right text/icon colour per theme (light → dark pill + white
  // label; dark → bordered surface + light label). We only add a soft shadow.
  const blackBtnStyle: ViewStyle = {
    ...Platform.select({
      web: { boxShadow: '0 1px 2px rgba(0,0,0,0.18)', transition: 'all 160ms ease' } as any,
      default: {},
    }),
  };
  const ActionBtn = ({ children, style: s, icon, ...rest }: any) => (
    <Button
      kind="primary"
      hoverLift
      icon={icon}
      {...rest}
      style={[blackBtnStyle, s]}
    >
      {children}
    </Button>
  );

  // Max width for field inputs so they don't stretch the full screen.
  const fieldMaxWidth = 560;

  if (loading) {
    return (
      <View style={{ flex: 1, alignItems: 'center', justifyContent: 'center', backgroundColor: colors.paper }}>
        <ActivityIndicator size="large" color={colors.accent} />
      </View>
    );
  }

  // CC-002 CR-7 — `isDeactivated` used to gate the Deactivate menu item. It is
  // gone: the gate is now `canDeactivateNow`, derived from the transition map, so
  // an ARCHIVED item correctly offers nothing instead of offering a Deactivate
  // that would 409.

  // Block Save/Create while any required (non-media) field is still empty.
  const isEmptyVal = (v: any) => v === undefined || v === null || v === '' || (Array.isArray(v) && v.length === 0);
  const missingRequired = fields.some(
    f => f.isRequired && f.fieldType !== 'MEDIA' && isEmptyVal(vals[f.fieldKey]),
  );

  // Bundle pricing: rolled-up total from parts + a currency formatter.
  const fmtPrice = (n: number | null) => (n == null ? '—' : '₹' + Number(n).toLocaleString('en-IN'));
  const partsTotal = components.reduce((sum, c) => {
    const p = compPrices[c.childItemId];
    return p == null ? sum : sum + p * (Number(c.quantity) || 1);
  }, 0);

  return (
    <ScrollView style={{ flex: 1, backgroundColor: colors.paper }} contentContainerStyle={{ padding: 24, paddingBottom: 64, maxWidth: 920, width: '100%', alignSelf: 'center' }}>
      {/* Breadcrumb */}
      <View style={{ flexDirection: 'row', alignItems: 'center', gap: 8, marginBottom: 14 }}>
        <TouchableOpacity onPress={() => navigation.navigate('items')}>
          <Text style={{ fontSize: fontSize.sm, color: colors.ink3, fontFamily: font.sans }}>Items</Text>
        </TouchableOpacity>
        <Icon name="chev-r" size={11} color={colors.ink3} />
        <Text style={{ fontSize: fontSize.sm, color: colors.ink, fontFamily: font.sans }}>
          {isNew ? `New ${typeName}` : (item?.skuOrReference || item?.id?.slice(0, 8))}
        </Text>
      </View>

      {/* Title row */}
      <View style={{ flexDirection: 'row', alignItems: 'flex-start', justifyContent: 'space-between', flexWrap: 'wrap', gap: 16, marginBottom: 20 }}>
        <View style={{ flex: 1, minWidth: 200 }}>
          <Text style={{ fontSize: fontSize['2xl'], fontWeight: fontWeight.semibold, color: colors.ink, fontFamily: font.sans, marginBottom: 6 }} numberOfLines={1}>
            {isNew ? `New ${typeName}` : (vals.title || item?.skuOrReference || 'Item')}
          </Text>
          <View style={{ flexDirection: 'row', flexWrap: 'wrap', gap: 8, alignItems: 'center' }}>
            <StateBadge state={state as any} />
            <Chip tone="neutral">{typeName}</Chip>
            {/* CC-002 CR-6 · DB-001 §4 — "a deactivated item clearly reads as
                not-sellable wherever it appears". The chip's form carries it
                visually; this spells it out, because the consequence (no price
                is quoted for it) is not obvious from the word "Deactivated". */}
            {!isNew && !isSellable && (
              <View style={{ flexDirection: 'row', alignItems: 'center', gap: 5 }}>
                <Icon name="alert-circle" size={12} color={colors.ink3} />
                <Text style={{ fontSize: fontSize.xs, color: colors.ink3, fontFamily: font.sans }}>
                  {notSellableNote}
                </Text>
              </View>
            )}
            {!isNew && (
              <Text style={{ fontFamily: font.mono, fontSize: fontSize.xs, color: colors.ink3 }}>
                v{String(item?.version ?? 1)} · updated {item?.updatedAt ? new Date(item.updatedAt).toLocaleDateString() : '—'}
              </Text>
            )}
          </View>
        </View>

        <View style={{ flexDirection: 'row', gap: 8, alignItems: 'center', flexShrink: 0 }}>
          <ActionBtn icon="check" disabled={busy || (!dirty && !isNew) || missingRequired} onPress={requestSave}>
            {busy ? 'Saving…' : isNew ? 'Create' : 'Save'}
          </ActionBtn>
          {/* CC-002 CR-1/CR-7 — Publish lives here because this is the only
              screen carrying the HSN/SAC input, so a refusal can point at the
              box that fixes it. Rendered only when the transition is LEGAL from
              the current state (DB-001 §4: don't offer, don't error). */}
          {!isNew && canPublish && (
            <ActionBtn icon="zap" disabled={busy || dirty} onPress={doPublish}>
              {state === 'PUBLISHED' ? 'Re-publish' : 'Publish'}
            </ActionBtn>
          )}
          {!isNew && (
            <Popover align="right" width={190} trigger={<Button iconOnly icon={<Icon name="more" size={14} color={colors.ink2} />} />}>
              {(close: () => void) => (
                <>
                  <MenuItem icon="history" onPress={() => { close(); loadVersions(); }}>Version history</MenuItem>
                  <MenuItem icon="copy" onPress={() => { close(); toast.success('Item ID copied'); }}>Copy item ID</MenuItem>
                  {/* CC-002 CR-7: gated on the transition map, not on a
                      hand-maintained `!isDeactivated` — an ARCHIVED item now
                      correctly offers nothing. */}
                  {canDeactivateNow && (
                    <>
                      <MenuSeparator />
                      <MenuItem icon="bolt-off" danger onPress={() => { close(); setDeactOpen(true); }}>Deactivate</MenuItem>
                    </>
                  )}
                </>
              )}
            </Popover>
          )}
        </View>
      </View>

      {/* Reference */}
      <View style={sectionCard}>
        <View style={sectionHead}>
          <Text style={{ fontSize: fontSize.md, fontWeight: fontWeight.semibold, color: colors.ink, fontFamily: font.sans }}>Identity</Text>
        </View>
        <View style={{ padding: 16, gap: 6 }}>
          <Text style={{ fontSize: fontSize.sm, fontWeight: fontWeight.medium, color: colors.ink2, fontFamily: font.sans }}>SKU / Reference</Text>
          <View style={{ maxWidth: fieldMaxWidth }}>
            <CommonInput value={sku} onChangeText={(t) => { setSku(t); setDirty(true); }} placeholder="e.g. SKU-001" isDark={isDark} />
          </View>
        </View>
      </View>

      {/* ══ CC-002 CR-1 · Tax (HSN/SAC) — SKCATALOG-DB-001 §1 ══════════════════
          Shown for taxable items and for items whose type says nothing about
          taxability; hidden entirely for an EXEMPT item, which has no code to
          carry and should not be asked for one.

          The block below is INLINE and ACTIONABLE — the exact thing the design
          brief rules out being a generic validation toast. It appears next to
          the input that clears it, states what to do, and disappears the moment
          a code is typed. */}
      {!isNew && showHsnCard && (
        <View style={sectionCard}>
          <View style={sectionHead}>
            <View style={{ flexDirection: 'row', alignItems: 'center', gap: 8 }}>
              <Text style={{ fontSize: fontSize.md, fontWeight: fontWeight.semibold, color: colors.ink, fontFamily: font.sans }}>
                Tax
              </Text>
              {hsnRequired && (
                <Chip tone={hsnMissing ? 'warn' : 'success'}>
                  {hsnMissing ? 'HSN/SAC needed' : 'GST ready'}
                </Chip>
              )}
            </View>
          </View>
          <View style={{ padding: 16, gap: 8 }}>
            <View style={{ flexDirection: 'row', alignItems: 'center', gap: 6 }}>
              <Text style={{ fontSize: fontSize.sm, fontWeight: fontWeight.medium, color: colors.ink2, fontFamily: font.sans }}>
                HSN / SAC code
              </Text>
              {hsnRequired && <Text style={{ color: colors.danger }}>*</Text>}
            </View>
            <View style={{ maxWidth: 240 }}>
              <CommonInput
                value={hsnSac}
                onChangeText={(t) => {
                  setHsnSac(t);
                  setDirty(true);
                  // Clear the block as soon as there is something to publish
                  // with — a warning that outstays its cause trains people to
                  // ignore warnings.
                  if (hasHsnSac(t)) setHsnBlocked(false);
                }}
                placeholder="e.g. 998213"
                isDark={isDark}
                // Mono + LTR: an HSN/SAC code is a digit string, and mono keeps
                // the digits aligned so a transposition is visible.
                inputProps={{ style: { fontFamily: font.mono, writingDirection: 'ltr' } }}
              />
            </View>

            {/* The inline block. */}
            {(hsnBlocked || (hsnRequired && hsnMissing)) && (
              <View
                style={{
                  flexDirection: 'row',
                  gap: 8,
                  padding: 12,
                  borderRadius: radii.r2,
                  borderWidth: 1,
                  borderColor: colors.warnLine,
                  backgroundColor: colors.warnSoft,
                }}
              >
                <Icon name="alert-circle" size={14} color={colors.warn} />
                <View style={{ flex: 1, gap: 2 }}>
                  <Text style={{ fontSize: fontSize.sm, fontWeight: fontWeight.medium, color: colors.ink, fontFamily: font.sans }}>
                    {HSN_REQUIRED_MESSAGE}
                  </Text>
                  <Text style={{ fontSize: fontSize.xs, color: colors.ink3, fontFamily: font.sans }}>
                    Save the code, then publish. The catalog only checks that a code is
                    present — the correct digit depth is applied when the invoice is raised.
                  </Text>
                </View>
              </View>
            )}

            {taxability === 'UNKNOWN' && (
              <Text style={{ fontSize: fontSize.xs, color: colors.ink3, fontFamily: font.sans }}>
                This type doesn't say whether its items are taxable. Add an
                “is_taxable” field to require an HSN/SAC code before publishing.
              </Text>
            )}
          </View>
        </View>
      )}

      {/* ══ CC-002 CR-5 · Price — SKCATALOG-DB-001 §3 ═══════════════════════════
          Current price prominent; the dated ladder behind a quiet affordance.
          The line about a new price being created is the point of the card: base
          price is effective-dated now, so old figures persist on purpose, and an
          operator who doesn't know that reads the history as duplicate rows. */}
      {!isNew && basePrice && (
        <View style={sectionCard}>
          <View style={sectionHead}>
            <View style={{ flexDirection: 'row', alignItems: 'center', gap: 8, flex: 1 }}>
              <Text style={{ fontSize: fontSize.md, fontWeight: fontWeight.semibold, color: colors.ink, fontFamily: font.sans }}>
                Price
              </Text>
              {!isSellable && <Chip tone="neutral">Not sellable</Chip>}
            </View>
            <TouchableOpacity
              onPress={() => (priceHistoryOpen ? setPriceHistoryOpen(false) : loadPriceHistory())}
              accessibilityLabel={priceHistoryOpen ? 'Hide price history' : 'Show price history'}
              style={{ flexDirection: 'row', alignItems: 'center', gap: 5, ...(Platform.OS === 'web' ? { cursor: 'pointer' } as any : {}) }}
            >
              <Icon name="history" size={13} color={colors.ink3} />
              <Text style={{ fontSize: fontSize.xs, color: colors.ink3, fontFamily: font.sans }}>
                {loadingPriceHistory ? 'Loading…' : priceHistoryOpen ? 'Hide history' : 'History'}
              </Text>
            </TouchableOpacity>
          </View>
          <View style={{ padding: 16, gap: 10 }}>
            {/* Prominent, mono, LTR — per the suite spec for amounts. */}
            <Text
              style={{
                fontFamily: font.mono,
                writingDirection: 'ltr',
                fontSize: fontSize['2xl'],
                fontWeight: fontWeight.semibold,
                color: colors.ink,
              }}
            >
              {formatMoney(basePrice.price)}
            </Text>
            <Text style={{ fontSize: fontSize.xs, color: colors.ink3, fontFamily: font.sans }}>
              {basePrice.effectiveFrom
                ? `In force since ${new Date(basePrice.effectiveFrom).toLocaleDateString()}.`
                : 'No dated price recorded yet.'}
              {' '}Editing the price creates a new price effective now; the previous
              price is kept for reporting.
            </Text>

            {priceHistoryOpen && (
              <View style={{ borderTopWidth: 1, borderTopColor: colors.line, paddingTop: 10, gap: 2 }}>
                {(priceHistory ?? []).length === 0 ? (
                  <Text style={{ fontSize: fontSize.sm, color: colors.ink3, fontFamily: font.sans }}>
                    No price changes recorded yet.
                  </Text>
                ) : (
                  (priceHistory ?? []).map((row, i) => (
                    <View
                      key={`${row.effectiveFrom}-${i}`}
                      style={{ flexDirection: 'row', alignItems: 'center', gap: 12, paddingVertical: 7, borderBottomWidth: i === (priceHistory!.length - 1) ? 0 : 1, borderBottomColor: colors.line }}
                    >
                      <Text style={{ width: 110, fontFamily: font.mono, writingDirection: 'ltr', fontSize: fontSize.sm, color: colors.ink }}>
                        {formatMoney(row.price)}
                      </Text>
                      <Text style={{ flex: 1, fontFamily: font.mono, writingDirection: 'ltr', fontSize: fontSize.xs, color: colors.ink3 }}>
                        {new Date(row.effectiveFrom).toLocaleDateString()}
                        {' → '}
                        {row.effectiveTo ? new Date(row.effectiveTo).toLocaleDateString() : 'now'}
                      </Text>
                      {/* The open row is the one in force; label it so a reader
                          doesn't have to infer it from the missing end date. */}
                      {!row.effectiveTo && <Chip tone="success">Current</Chip>}
                    </View>
                  ))
                )}
              </View>
            )}
          </View>
        </View>
      )}

      {/* Fields */}
      <View style={sectionCard}>
        <View style={sectionHead}>
          <View style={{ flexDirection: 'row', alignItems: 'center', gap: 8 }}>
            <Text style={{ fontSize: fontSize.md, fontWeight: fontWeight.semibold, color: colors.ink, fontFamily: font.sans }}>Fields</Text>
            <Chip tone="neutral" textStyle={{ color: colors.ink, fontWeight: fontWeight.semibold }}>{fields.filter(f => f.fieldType !== 'MEDIA').length}</Chip>
          </View>
        </View>
        <View style={{ padding: 16, gap: 14 }}>
          {fields.filter(f => f.fieldType !== 'MEDIA').length === 0 ? (
            <Empty title="No fields defined">
              Add a field to “{typeName}” to start describing this item.
            </Empty>
          ) : (
            // MEDIA fields are edited in the Media section below, not here.
            fields.filter(f => f.fieldType !== 'MEDIA').map((f) => (
              <View key={f.fieldKey} style={{ gap: 6 }}>
                <View style={{ flexDirection: 'row', alignItems: 'center', gap: 6 }}>
                  <Text style={{ fontSize: fontSize.sm, fontWeight: fontWeight.medium, color: colors.ink2, fontFamily: font.sans }}>
                    {f.fieldKey}
                  </Text>
                  {f.isRequired && <Text style={{ color: colors.danger }}>*</Text>}
                  {/* CC-002 CR-2 — a locked field says so at a glance, even in
                      the brand editor where it is still editable. Without this
                      the only way to know a field is a brand standard is to open
                      the toggle's tooltip. */}
                  {f.isLocked && (
                    <View style={{ flexDirection: 'row', alignItems: 'center', gap: 4 }}>
                      <Icon name="lock" size={11} color={colors.ink4} />
                      <Text style={{ fontSize: fontSize.xs, color: colors.ink4, fontFamily: font.sans }}>
                        Brand standard
                      </Text>
                    </View>
                  )}
                  <View style={{ flex: 1 }} />
                  {/* ── CC-002 CR-2 · lock toggle (SKCATALOG-DB-001 §2) ──────
                      Offered only for LOCKABLE fields. Price fields get no
                      toggle at all — local price flexibility is the design, and
                      an offered control that always errors is worse than no
                      control, because the operator reads the failure as a bug.
                      `isLockableField` mirrors the API's own predicate. */}
                  {isLockableField(f) && (
                    <TouchableOpacity
                      onPress={() => toggleFieldLock(f.fieldKey, !f.isLocked)}
                      disabled={lockingField === f.fieldKey}
                      accessibilityLabel={
                        f.isLocked
                          ? `Unlock ${f.fieldKey} so locations can override it`
                          : `Lock ${f.fieldKey} as a brand standard`
                      }
                      hitSlop={{ top: 8, bottom: 8, left: 8, right: 8 }}
                      style={{
                        width: 26,
                        height: 26,
                        borderRadius: radii.r2,
                        alignItems: 'center',
                        justifyContent: 'center',
                        opacity: lockingField === f.fieldKey ? 0.5 : 1,
                        ...(Platform.OS === 'web' ? { cursor: 'pointer' } as any : {}),
                      }}
                    >
                      <Icon
                        name="lock"
                        size={13}
                        color={f.isLocked ? colors.accent : colors.ink4}
                      />
                    </TouchableOpacity>
                  )}
                  {/* Delete this field from the type */}
                  <TouchableOpacity
                    onPress={() => setDelField(f.fieldKey)}
                    accessibilityLabel={`Delete field ${f.fieldKey}`}
                    hitSlop={{ top: 8, bottom: 8, left: 8, right: 8 }}
                    style={{ width: 26, height: 26, borderRadius: radii.r2, alignItems: 'center', justifyContent: 'center', ...(Platform.OS === 'web' ? { cursor: 'pointer' } as any : {}) }}
                  >
                    <Icon name="trash" size={14} color={colors.danger} />
                  </TouchableOpacity>
                </View>
                <View style={{ maxWidth: fieldMaxWidth }}>
                  <FieldControl
                    field={{
                      key: f.fieldKey,
                      type: f.fieldType,
                      label: f.fieldKey,
                      required: !!f.isRequired,
                      localized: !!f.isLocalized,
                      options: f.config?.options ?? [],
                      unit: f.config?.unit,
                    } as FieldDef}
                    value={vals[f.fieldKey]}
                    onChange={(v) => setField(f.fieldKey, v)}
                  />
                  {/* CC-002 CR-2 — the brand scope keeps editing (it owns the
                      value); this explains what the lock DOES, since the field
                      being editable here could otherwise read as the lock not
                      having taken effect. */}
                  {f.isLocked && (
                    <Text style={{ marginTop: 6, fontSize: fontSize.xs, color: colors.ink3, fontFamily: font.sans }}>
                      Locked as a brand standard — locations see “{LOCKED_HELPER_TEXT}” and
                      can't override it. You're editing the brand value.
                    </Text>
                  )}
                  {/* For dropdown fields: manage option values (add + delete) */}
                  {(f.fieldType === 'ENUM' || f.fieldType === 'MULTI_SELECT') && (
                    <View style={{ flexDirection: 'row', flexWrap: 'wrap', alignItems: 'center', gap: 8, marginTop: 8 }}>
                      {(f.config?.options ?? []).map((opt: string) => (
                        <View key={opt} style={{ flexDirection: 'row', alignItems: 'center', gap: 6, paddingLeft: 12, paddingRight: 6, paddingVertical: 6, borderRadius: 999, borderWidth: 1, borderColor: colors.line, backgroundColor: colors.paper2 }}>
                          <Text style={{ fontSize: fontSize.sm, color: colors.ink2, fontFamily: font.sans }}>{opt}</Text>
                          <TouchableOpacity
                            onPress={() => setDelOption({ fieldKey: f.fieldKey, opt })}
                            accessibilityLabel={`Remove option ${opt}`}
                            hitSlop={{ top: 6, bottom: 6, left: 4, right: 6 }}
                            style={{ padding: 2, ...(Platform.OS === 'web' ? { cursor: 'pointer' } as any : {}) }}
                          >
                            <Icon name="x" size={12} color={colors.ink3} />
                          </TouchableOpacity>
                        </View>
                      ))}
                      <TouchableOpacity
                        activeOpacity={0.85}
                        onPress={() => { setOptionValue(''); setAddOptionFor(f.fieldKey); }}
                        style={{
                          flexDirection: 'row',
                          alignItems: 'center',
                          gap: 6,
                          paddingVertical: 6,
                          paddingLeft: 8,
                          paddingRight: 12,
                          borderRadius: 999,
                          borderWidth: 1,
                          borderColor: colors.accentLine ?? colors.accent,
                          backgroundColor: colors.accentSoft,
                          ...Platform.select({
                            web: { cursor: 'pointer', transition: 'all 140ms ease', boxShadow: '0 1px 2px rgba(99,102,241,0.18)' } as any,
                            default: {},
                          }),
                        }}
                      >
                        <View style={{ width: 18, height: 18, borderRadius: 9, backgroundColor: colors.accent, alignItems: 'center', justifyContent: 'center' }}>
                          <Icon name="plus" size={11} color={colors.white} />
                        </View>
                        <Text style={{ fontSize: fontSize.sm, fontWeight: fontWeight.semibold, color: colors.accentInk ?? colors.accent, fontFamily: font.sans }}>Add option</Text>
                      </TouchableOpacity>
                    </View>
                  )}
                </View>
              </View>
            ))
          )}
        </View>
      </View>

      {/* Media (MEDIA-type fields, minus any cleared this session) */}
      {fields.some(f => f.fieldType === 'MEDIA' && !clearedMedia.includes(f.fieldKey)) && (
        <View style={sectionCard}>
          <View style={sectionHead}>
            <Text style={{ fontSize: fontSize.md, fontWeight: fontWeight.semibold, color: colors.ink, fontFamily: font.sans }}>Media</Text>
          </View>
          <View style={{ padding: 16, gap: 14 }}>
            {isNew && (
              <Text style={{ fontSize: fontSize.xs, color: colors.ink3, fontFamily: font.sans }}>
                Save the item first to upload media.
              </Text>
            )}
            {fields.filter(f => f.fieldType === 'MEDIA' && !clearedMedia.includes(f.fieldKey)).map(f => {
              const st = mediaStatus[f.fieldKey];
              return (
                <View key={f.fieldKey} style={{ gap: 8 }}>
                  <View style={{ flexDirection: 'row', alignItems: 'center', gap: 8 }}>
                    <Text style={{ fontSize: fontSize.sm, fontWeight: fontWeight.medium, color: colors.ink2, fontFamily: font.sans }}>{f.fieldKey}</Text>
                    {st?.status && <Chip tone={st.status === 'READY' ? 'success' : 'neutral'}>{st.status}</Chip>}
                    <View style={{ flex: 1 }} />
                    {/* Delete this media field from the type */}
                    <TouchableOpacity
                      onPress={() => setDelField(f.fieldKey)}
                      accessibilityLabel={`Delete field ${f.fieldKey}`}
                      hitSlop={{ top: 8, bottom: 8, left: 8, right: 8 }}
                      style={{ width: 26, height: 26, borderRadius: radii.r2, alignItems: 'center', justifyContent: 'center', ...(Platform.OS === 'web' ? { cursor: 'pointer' } as any : {}) }}
                    >
                      <Icon name="trash" size={14} color={colors.danger} />
                    </TouchableOpacity>
                  </View>
                  {/* Image preview — cdn url once READY, else the instant local preview */}
                  {(() => {
                    const src = st?.cdnUrl || st?.url || st?.localUrl;
                    if (!src) return null;
                    return (
                      <Image
                        source={{ uri: src }}
                        style={{ width: 160, height: 160, borderRadius: radii.r2, backgroundColor: colors.paper2, borderWidth: 1, borderColor: colors.line }}
                        resizeMode="cover"
                      />
                    );
                  })()}
                  {st?.cdnUrl || st?.url ? (
                    <Text style={{ fontFamily: font.mono, fontSize: fontSize.xs, color: colors.ink3 }} numberOfLines={1}>{st.cdnUrl || st.url}</Text>
                  ) : null}
                  <View style={{ flexDirection: 'row', gap: 8 }}>
                    <ActionBtn size="sm" icon="upload" disabled={isNew} onPress={() => uploadMedia(f.fieldKey)}>Upload</ActionBtn>
                    <ActionBtn size="sm" icon="refresh" disabled={isNew} onPress={() => refreshMedia(f.fieldKey)}>Status</ActionBtn>
                    <ActionBtn size="sm" icon="trash" disabled={isNew} onPress={() => setClearMediaKey(f.fieldKey)}>Clear</ActionBtn>
                  </View>
                </View>
              );
            })}
          </View>
        </View>
      )}

      {/* Taxonomy assignment (categories + tags) */}
      {(
        <View style={sectionCard}>
          <View style={sectionHead}>
            <Text style={{ fontSize: fontSize.md, fontWeight: fontWeight.semibold, color: colors.ink, fontFamily: font.sans }}>Categories &amp; tags</Text>
            <ActionBtn size="sm" icon="check" disabled={savingTax || isNew} onPress={saveTaxonomy}>
              {savingTax ? 'Saving…' : 'Save assignments'}
            </ActionBtn>
          </View>
          <View style={{ padding: 16, gap: 14 }}>
            {isNew && (
              <Text style={{ fontSize: fontSize.xs, color: colors.ink3, fontFamily: font.sans }}>
                Save the item first to assign categories &amp; tags.
              </Text>
            )}
            <View style={{ gap: 8 }}>
              <View style={{ flexDirection: 'row', alignItems: 'center', justifyContent: 'space-between' }}>
                <Text style={{ fontSize: fontSize.sm, fontWeight: fontWeight.medium, color: colors.ink2, fontFamily: font.sans }}>Categories</Text>
                <TouchableOpacity
                  onPress={() => { setCatName(''); setCatParentId(''); setAddingCat(true); }}
                  style={{ width: 28, height: 28, borderRadius: 8, backgroundColor: colors.card, borderWidth: 1, borderColor: colors.lineStrong, alignItems: 'center', justifyContent: 'center', ...Platform.select({ web: { cursor: 'pointer' } as any, default: {} }) }}
                >
                  <Icon name="plus" size={14} color={colors.ink2} />
                </TouchableOpacity>
              </View>
              {allCategories.length === 0 ? (
                <Text style={{ fontSize: fontSize.sm, color: colors.ink3, fontFamily: font.sans }}>No categories yet.</Text>
              ) : (
                <View style={{ flexDirection: 'row', flexWrap: 'wrap', gap: 8 }}>
                  {allCategories.map(c => {
                    const on = selCats.has(c.id);
                    return (
                      <View key={c.id} style={{ flexDirection: 'row', alignItems: 'center', gap: 6, paddingLeft: 12, paddingRight: 6, paddingVertical: 6, borderRadius: 999, borderWidth: 1, borderColor: on ? colors.accent : colors.line, backgroundColor: on ? colors.accentSoft : colors.card }}>
                        <TouchableOpacity onPress={() => toggleSet(setSelCats, c.id)}>
                          <Text style={{ fontSize: fontSize.sm, color: on ? colors.accentInk : colors.ink2, fontFamily: font.sans }}>{c.name}</Text>
                        </TouchableOpacity>
                        {/* Add a sub-category under this category */}
                        <TouchableOpacity onPress={() => { setCatName(''); setCatParentId(c.id); setAddingCat(true); }} hitSlop={{ top: 6, bottom: 6, left: 4, right: 4 }} style={{ padding: 2 }}>
                          <Icon name="plus" size={12} color={on ? colors.accentInk : colors.ink3} />
                        </TouchableOpacity>
                        <TouchableOpacity onPress={() => { setEditTaxon({ kind: 'category', id: c.id, name: c.name }); setEditTaxonName(c.name); }} hitSlop={{ top: 6, bottom: 6, left: 4, right: 4 }} style={{ padding: 2 }}>
                          <Icon name="edit" size={12} color={on ? colors.accentInk : colors.ink3} />
                        </TouchableOpacity>
                        <TouchableOpacity onPress={() => setDelTaxon({ kind: 'category', id: c.id, name: c.name })} hitSlop={{ top: 6, bottom: 6, left: 4, right: 6 }} style={{ padding: 2 }}>
                          <Icon name="x" size={12} color={on ? colors.accentInk : colors.ink3} />
                        </TouchableOpacity>
                      </View>
                    );
                  })}
                </View>
              )}
            </View>
            <View style={{ gap: 8 }}>
              <View style={{ flexDirection: 'row', alignItems: 'center', justifyContent: 'space-between' }}>
                <Text style={{ fontSize: fontSize.sm, fontWeight: fontWeight.medium, color: colors.ink2, fontFamily: font.sans }}>Tags</Text>
                <TouchableOpacity
                  onPress={() => { setTagName(''); setAddingTag(true); }}
                  style={{ width: 28, height: 28, borderRadius: 8, backgroundColor: colors.card, borderWidth: 1, borderColor: colors.lineStrong, alignItems: 'center', justifyContent: 'center', ...Platform.select({ web: { cursor: 'pointer' } as any, default: {} }) }}
                >
                  <Icon name="plus" size={14} color={colors.ink2} />
                </TouchableOpacity>
              </View>
              {allTags.length === 0 ? (
                <Text style={{ fontSize: fontSize.sm, color: colors.ink3, fontFamily: font.sans }}>No tags yet.</Text>
              ) : (
                <View style={{ flexDirection: 'row', flexWrap: 'wrap', gap: 8 }}>
                  {allTags.map(t => {
                    const on = selTags.has(t.id);
                    return (
                      <View key={t.id} style={{ flexDirection: 'row', alignItems: 'center', gap: 6, paddingLeft: 12, paddingRight: 6, paddingVertical: 6, borderRadius: 999, borderWidth: 1, borderColor: on ? colors.accent : colors.line, backgroundColor: on ? colors.accentSoft : colors.card }}>
                        <TouchableOpacity onPress={() => toggleSet(setSelTags, t.id)}>
                          <Text style={{ fontSize: fontSize.sm, color: on ? colors.accentInk : colors.ink2, fontFamily: font.sans }}>{t.name}</Text>
                        </TouchableOpacity>
                        <TouchableOpacity onPress={() => { setEditTaxon({ kind: 'tag', id: t.id, name: t.name }); setEditTaxonName(t.name); }} hitSlop={{ top: 6, bottom: 6, left: 4, right: 4 }} style={{ padding: 2 }}>
                          <Icon name="edit" size={12} color={on ? colors.accentInk : colors.ink3} />
                        </TouchableOpacity>
                        <TouchableOpacity onPress={() => setDelTaxon({ kind: 'tag', id: t.id, name: t.name })} hitSlop={{ top: 6, bottom: 6, left: 4, right: 6 }} style={{ padding: 2 }}>
                          <Icon name="x" size={12} color={on ? colors.accentInk : colors.ink3} />
                        </TouchableOpacity>
                      </View>
                    );
                  })}
                </View>
              )}
            </View>
          </View>
        </View>
      )}

      {/* Collections — tap a collection to open its "Add items" manage card
          (same as the Collections screen); "+" / pencil / × create/rename/delete. */}
      <CatalogManager
        title="Collections"
        emptyText="No collections yet."
        chipHint="Click a collection to manage its items."
        loadAll={async () => ((await getCollections()).items ?? []).map((c: any) => ({ id: c.id, name: c.name }))}
        onCreate={(name) => createCollection({ name })}
        onRename={(id, name) => updateCollection(id, { name })}
        onDelete={deleteCollection}
        onChip={(c) => openCollManage({ id: c.id, name: c.name } as Collection)}
      />

      {/* Locations — org-wide; create/rename/delete */}
      <CatalogManager
        title="Locations"
        emptyText="No locations yet — add one in Setup › Locations."
        loadAll={async () => ((await getLocations()).items ?? []).map((l: any) => ({ id: l.id, name: l.name }))}
        // No create here: a location mirrors a branch platform owns, so it is
        // added by picking one in Setup > Locations, never typed by name.
        onRename={(id, name) => updateLocation(id, { name })}
        onDelete={deleteLocation}
      />

      {/* Bundle components — this item as a bundle parent */}
      <View style={[sectionCard, { marginTop: 16 }]}>
        <View style={sectionHead}>
          <View style={{ flexDirection: 'row', alignItems: 'center', gap: 8 }}>
            <Text style={{ fontSize: fontSize.md, fontWeight: fontWeight.semibold, color: colors.ink, fontFamily: font.sans }}>Bundle components</Text>
            <Chip tone="neutral" textStyle={{ color: colors.ink, fontWeight: fontWeight.semibold }}>{components.length}</Chip>
          </View>
          {!isNew && (
            <View style={{ flexDirection: 'row', gap: 8 }}>
              <ActionBtn size="sm" icon="plus" onPress={() => { setPickItemId(''); setBundleRelation('BUNDLE_DYNAMIC'); setBundleQty('1'); setAddCompOpen(true); }}>Add component</ActionBtn>
            </View>
          )}
        </View>
        <View style={{ padding: 16, gap: 12 }}>
          {isNew ? (
            <Text style={{ fontSize: fontSize.sm, color: colors.ink3, fontFamily: font.sans }}>Save the item first to add components.</Text>
          ) : components.length === 0 ? (
            <Text style={{ fontSize: fontSize.sm, color: colors.ink3, fontFamily: font.sans }}>No components. Use “Add component” to make this a bundle.</Text>
          ) : (
            <>
              {components.map((c) => {
                const unit = compPrices[c.childItemId];
                const q = Number(c.quantity) || 1;
                return (
                  <View key={c.id || c.childItemId} style={{ flexDirection: 'row', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 8, borderWidth: 1, borderColor: colors.line, borderRadius: radii.r2, paddingVertical: 8, paddingHorizontal: 12 }}>
                    <View style={{ flex: 1, minWidth: 140, flexDirection: 'row', alignItems: 'center', gap: 8 }}>
                      <Icon name="box" size={15} color={colors.ink3} />
                      <Text numberOfLines={1} style={{ flexShrink: 1, fontSize: fontSize.sm, color: colors.ink, fontFamily: font.sans }}>{labelForItem(c.childItemId)}</Text>
                      <Chip tone="outline">{c.relationType}</Chip>
                    </View>
                    <Text style={{ fontFamily: font.mono, fontSize: fontSize.sm, color: unit == null ? colors.ink3 : colors.ink, minWidth: 72, textAlign: 'right' }}>
                      {unit == null ? 'On enquiry' : fmtPrice(unit * q)}
                      {unit != null && q > 1 ? <Text style={{ color: colors.ink3 }}>{`  (${fmtPrice(unit)}×${q})`}</Text> : null}
                    </Text>
                    <View style={{ flexDirection: 'row', alignItems: 'center', gap: 6 }}>
                      <Button kind="ghost" size="xs" iconOnly icon="minus" aria-label="Decrease quantity" onPress={() => changeComponentQty(c, -1)} />
                      <Text style={{ fontFamily: font.mono, fontSize: fontSize.sm, color: colors.ink, minWidth: 24, textAlign: 'center' }}>×{c.quantity ?? 1}</Text>
                      <Button kind="ghost" size="xs" iconOnly icon="plus" aria-label="Increase quantity" onPress={() => changeComponentQty(c, 1)} />
                      <Button kind="ghost" size="xs" iconOnly icon="edit" aria-label="Edit component" onPress={() => openEditComponent(c)} />
                      <Button kind="danger" size="xs" iconOnly icon="trash" aria-label="Remove component" onPress={() => setDelComponent({ childItemId: c.childItemId, label: labelForItem(c.childItemId) })} />
                    </View>
                  </View>
                );
              })}
              {/* Rolled-up total + the bundle's own price (set manually or relink). */}
              <View style={{ flexDirection: 'row', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 8, borderTopWidth: 1, borderTopColor: colors.line, paddingTop: 12, marginTop: 4 }}>
                <Text style={{ fontSize: fontSize.sm, color: colors.ink2, fontFamily: font.sans }}>
                  Total from parts <Text style={{ color: colors.ink3 }}>(Σ price × qty)</Text>
                </Text>
                <Text style={{ fontFamily: font.mono, fontSize: fontSize.md, fontWeight: fontWeight.semibold, color: colors.ink }}>{fmtPrice(partsTotal)}</Text>
              </View>
              <View style={{ gap: 2 }}>
                <Text style={{ fontSize: fontSize.sm, color: colors.ink2, fontFamily: font.sans }}>
                  Bundle's current price <Text style={{ fontFamily: font.mono, color: colors.ink }}>{fmtPrice(currentPrice)}</Text>
                </Text>
                <Text style={{ fontSize: fontSize.xs, color: colors.ink3, fontFamily: font.sans }}>
                  Dynamic bundles roll up from their parts automatically.
                </Text>
              </View>
            </>
          )}
        </View>
      </View>

      {/* Bundles — the full named-bundle manager (create / select / rename / delete). */}
      <View style={[sectionCard, { marginTop: 16, padding: 16, gap: 12 }]}>
        <Text style={{ fontSize: fontSize.md, fontWeight: fontWeight.semibold, color: colors.ink, fontFamily: font.sans }}>Bundles</Text>
        <BundleManager />
      </View>

      {/* AI suggestions */}
      {(
        <View style={sectionCard}>
          <View style={sectionHead}>
            <Text style={{ fontSize: fontSize.md, fontWeight: fontWeight.semibold, color: colors.ink, fontFamily: font.sans }}>AI suggestions</Text>
            <View style={{ flexDirection: 'row', gap: 6 }}>
              <ActionBtn size="sm" icon="sparkles" onPress={runAiSuggestions} disabled={generating || isNew}>
                {generating ? 'Generating…' : 'Generate with AI'}
              </ActionBtn>
              <ActionBtn size="sm" icon="refresh" onPress={loadSuggestions} disabled={isNew}>Refresh</ActionBtn>
            </View>
          </View>
          <View style={{ padding: 16, gap: 10 }}>
            {isNew ? (
              <Text style={{ fontSize: fontSize.sm, color: colors.ink3, fontFamily: font.sans }}>Save the item first to generate AI suggestions.</Text>
            ) : suggestions.length === 0 ? (
              <Text style={{ fontSize: fontSize.sm, color: colors.ink3, fontFamily: font.sans }}>No pending suggestions for this item.</Text>
            ) : suggestions.map(s => (
              <View key={s.id} style={{ flexDirection: 'row', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 8, rowGap: 10, borderWidth: 1, borderColor: colors.line, borderRadius: radii.r2, padding: 12 }}>
                <View style={{ flex: 1, minWidth: 200 }}>
                  <View style={{ flexDirection: 'row', gap: 6, alignItems: 'flex-start' }}>
                    <Chip tone="accent">{s.kind}</Chip>
                    {/* Plain-English message describing what the AI proposes. */}
                    <Text style={{ fontSize: fontSize.sm, color: colors.ink, fontFamily: font.sans, flex: 1 }} numberOfLines={3}>
                      {suggestionMessage(s)}
                    </Text>
                  </View>
                  {s.confidence != null && (
                    <Text style={{ fontSize: fontSize.xs, color: colors.ink3, fontFamily: font.sans, marginTop: 4 }}>
                      AI · {Math.round(Number(s.confidence) * 100)}% confident
                    </Text>
                  )}
                </View>
                <View style={{ flexDirection: 'row', gap: 6, flexShrink: 0 }}>
                  <ActionBtn size="sm" icon="check" onPress={() => onAcceptSuggestion(s)}>Accept</ActionBtn>
                  <ActionBtn size="sm" icon="x" onPress={() => onRejectSuggestion(s)}>Reject</ActionBtn>
                </View>
              </View>
            ))}
          </View>
        </View>
      )}

      {/* Variants + Resolve + Export tools */}
      {!isNew && (
        <View style={sectionCard}>
          <View style={sectionHead}>
            <Text style={{ fontSize: fontSize.md, fontWeight: fontWeight.semibold, color: colors.ink, fontFamily: font.sans }}>Tools</Text>
          </View>
          <View style={{ padding: 16, gap: 12 }}>
            <View style={{ flexDirection: 'row', flexWrap: 'wrap', gap: 8 }}>
              <ActionBtn size="sm" icon="layers" onPress={loadVariants}>Load variants</ActionBtn>
              <ActionBtn size="sm" icon="eye" onPress={doResolve}>Preview (resolve)</ActionBtn>
              <ActionBtn size="sm" icon="download" onPress={doExport}>Export</ActionBtn>
            </View>

            {/* Price what-if + commit (propagates to ancestor bundles) */}
            <View style={{ borderTopWidth: 1, borderTopColor: colors.line, paddingTop: 12, gap: 8 }}>
              <Text style={{ fontSize: fontSize.sm, fontWeight: fontWeight.medium, color: colors.ink2, fontFamily: font.sans }}>Price propagation</Text>
              <View style={{ flexDirection: 'row', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
                <CommonInput value={priceValue} onChangeText={setPriceValue} placeholder="New price" isDark={isDark} containerStyle={{ width: 140 }} inputProps={{ keyboardType: 'numeric' }} />
                <ActionBtn size="sm" icon="eye" onPress={previewPrice}>Preview impact</ActionBtn>
                <ActionBtn size="sm" icon="check" onPress={commitNewPrice}>Commit price</ActionBtn>
              </View>
              {impact && (
                <View style={{ borderWidth: 1, borderColor: colors.line, borderRadius: radii.r2, padding: 10, backgroundColor: colors.paper2 }}>
                  <Text style={{ fontFamily: font.mono, fontSize: fontSize.xs, color: colors.ink2 }} numberOfLines={8}>
                    {JSON.stringify(impact, null, 2).slice(0, 500)}
                  </Text>
                </View>
              )}
            </View>
            {variants && (
              <Text style={{ fontFamily: font.mono, fontSize: fontSize.xs, color: colors.ink3 }}>
                {variants.length} variant(s)
              </Text>
            )}
            {resolved && (
              <View style={{ borderWidth: 1, borderColor: colors.line, borderRadius: radii.r2, padding: 10, backgroundColor: colors.paper2 }}>
                <Text style={{ fontFamily: font.mono, fontSize: fontSize.xs, color: colors.ink2 }} numberOfLines={8}>
                  {JSON.stringify(resolved, null, 2).slice(0, 600)}
                </Text>
              </View>
            )}
          </View>
        </View>
      )}

      {/* Version history — centered modal so it surfaces immediately. */}
      <Modal
        open={versions !== null}
        onClose={() => setVersions(null)}
        size="sm"
        title="Version history"
      >
        <View style={{ gap: 0 }}>
          {versions && versions.length === 0 ? (
            <Text style={{ fontSize: fontSize.sm, color: colors.ink3, fontFamily: font.sans }}>No version records yet.</Text>
          ) : (versions || []).map((v, i) => {
            // All values come straight from the backend version record.
            const p: any = v.payload ?? {};
            const by: any = p.changedBy ?? {};
            const email: string = by.email ?? '';
            const role: string = by.role ?? '';
            const operation: string = p.changes?.operation ?? '';
            const label = String(v.eventType ?? '');
            // Backend already provides a formatted IST string — use it verbatim.
            const dateStr: string = p.occurredAtIST ?? p.occurredAt ?? v.occurredAt ?? '';
            // Initials from the email local-part (e.g. gokulakrishnan.agk14 → GA).
            const localPart = (email.split('@')[0] || '');
            const initials = (localPart
              ? (localPart.split(/[._-]+/).filter(Boolean).map((s: string) => s[0]).slice(0, 2).join('') || localPart.slice(0, 2))
              : '?').toUpperCase();
            const roleLabel = role ? role.replace(/_/g, ' ') : '';
            // Events with no `changedBy` (e.g. MEDIA_PENDING) are automated —
            // show them as a System actor instead of an empty "?".
            const isSystem = !email;
            const isLast = i === (versions || []).length - 1;
            return (
              <View key={i} style={{ flexDirection: 'row', gap: 12, paddingVertical: 12, borderBottomWidth: isLast ? 0 : 1, borderBottomColor: colors.line }}>
                {/* Actor avatar — initials for a user, a gear for system events */}
                <View style={{ width: 36, height: 36, borderRadius: 18, backgroundColor: isSystem ? colors.paper3 : colors.accentSoft, borderWidth: 1, borderColor: isSystem ? colors.line : colors.accentLine, alignItems: 'center', justifyContent: 'center' }}>
                  {isSystem
                    ? <Icon name="settings" size={15} color={colors.ink3} />
                    : <Text style={{ fontSize: fontSize.xs, fontWeight: fontWeight.semibold, color: colors.accentInk, fontFamily: font.sans }}>{initials}</Text>}
                </View>
                <View style={{ flex: 1, minWidth: 0, gap: 3 }}>
                  <View style={{ flexDirection: 'row', alignItems: 'center', justifyContent: 'space-between', gap: 8 }}>
                    <View style={{ flexDirection: 'row', alignItems: 'center', gap: 8, flexShrink: 1, minWidth: 0 }}>
                      <Text numberOfLines={1} style={{ fontFamily: font.mono, fontSize: fontSize.xs, fontWeight: fontWeight.semibold, color: colors.ink, letterSpacing: 0.3 }}>{label}</Text>
                      {!!operation && <Chip tone="neutral">{operation}</Chip>}
                    </View>
                    {!!dateStr && <Text numberOfLines={1} style={{ fontSize: fontSize.xs, color: colors.ink4, fontFamily: font.sans }}>{dateStr}</Text>}
                  </View>
                  <Text numberOfLines={1} style={{ fontSize: fontSize.sm, fontWeight: fontWeight.medium, color: colors.ink2, fontFamily: font.sans }}>
                    {email || 'System'}
                  </Text>
                  <Text numberOfLines={1} style={{ fontSize: fontSize.xs, color: colors.ink3, fontFamily: font.sans, textTransform: 'capitalize' }}>
                    {roleLabel || (isSystem ? 'Automated event' : '')}
                  </Text>
                </View>
              </View>
            );
          })}
        </View>
      </Modal>

      {/* Bottom action bar — Save */}
      <View style={{ flexDirection: 'row', justifyContent: 'flex-end', alignItems: 'center', gap: 10, marginTop: 20 }}>
        <ActionBtn icon="check" disabled={busy || (!dirty && !isNew) || missingRequired} onPress={requestSave}>
          {busy ? 'Saving…' : isNew ? 'Create' : 'Save'}
        </ActionBtn>
      </View>

      {/* Confirm-before-save — lets the user review the field values they're
          about to commit before the PATCH/create actually fires. */}
      <ConfirmDialog
        visible={confirmSaveOpen}
        title={isNew ? `Create this ${typeName}?` : 'Save these changes?'}
        message={
          isNew
            ? 'Please confirm the details below are correct. The item will be created as a draft.'
            : 'Please confirm the details below are correct before saving.'
        }
        confirmText={busy ? 'Saving…' : isNew ? 'Create' : 'Save'}
        cancelText="Review again"
        confirmColor={colors.accent}
        onConfirm={save}
        onCancel={() => setConfirmSaveOpen(false)}
        onClose={() => setConfirmSaveOpen(false)}
      >
        {(() => {
          const review: { label: string; value: string }[] = [];
          if (sku) review.push({ label: 'SKU / Reference', value: sku });
          for (const f of fields) {
            if (f.fieldType === 'MEDIA') continue;
            const v = vals[f.fieldKey];
            if (isEmptyVal(v)) continue;
            review.push({
              label: (f as any).label || f.fieldKey,
              value: Array.isArray(v) ? v.join(', ') : String(v),
            });
          }
          return (
            <View
              style={{
                alignSelf: 'stretch',
                marginTop: 14,
                maxHeight: 220,
                borderWidth: 1,
                borderColor: colors.line,
                borderRadius: radii.r2,
                backgroundColor: colors.paper2,
              }}
            >
              {review.length === 0 ? (
                <Text style={{ fontSize: fontSize.sm, color: colors.ink3, fontFamily: font.sans, padding: 12, textAlign: 'center' }}>
                  No field values entered yet.
                </Text>
              ) : (
                <ScrollView style={{ maxHeight: 220 }} contentContainerStyle={{ padding: 4 }}>
                  {review.map((r, i) => (
                    <View
                      key={r.label + i}
                      style={{
                        flexDirection: 'row',
                        justifyContent: 'space-between',
                        gap: 12,
                        paddingVertical: 8,
                        paddingHorizontal: 8,
                        borderBottomWidth: i === review.length - 1 ? 0 : 1,
                        borderBottomColor: colors.line,
                      }}
                    >
                      <Text style={{ fontSize: fontSize.sm, color: colors.ink3, fontFamily: font.sans, flexShrink: 0 }}>
                        {r.label}
                      </Text>
                      <Text
                        style={{ fontSize: fontSize.sm, fontWeight: fontWeight.medium, color: colors.ink, fontFamily: font.sans, flex: 1, textAlign: 'right' }}
                        numberOfLines={2}
                      >
                        {r.value}
                      </Text>
                    </View>
                  ))}
                </ScrollView>
              )}
            </View>
          );
        })()}
      </ConfirmDialog>

      {/* Deactivate modal */}
      <Modal
        open={deactOpen}
        onClose={() => setDeactOpen(false)}
        size="sm"
        title="Deactivate item"
        footer={
          <View style={{ flexDirection: 'row', gap: 8, justifyContent: 'flex-end' }}>
            <Button onPress={() => setDeactOpen(false)}>Cancel</Button>
            <Button kind="accent" icon="bolt-off" disabled={busy} onPress={doDeactivate}>
              {busy ? 'Working…' : 'Deactivate'}
            </Button>
          </View>
        }
      >
        <View style={{ gap: 10 }}>
          <Text style={{ fontSize: fontSize.md, color: colors.ink2, fontFamily: font.sans }}>
            This removes the item from the live catalog. You can provide a reason (optional).
          </Text>
          <CommonInput value={deactReason} onChangeText={setDeactReason} placeholder="Reason (optional)" isDark={isDark} />
        </View>
      </Modal>

      {/* Manage a collection's items (opened from the Collections card) */}
      <Modal
        open={!!manageColl}
        onClose={() => setManageColl(null)}
        size="wide"
        title={manageColl ? `Manage — ${manageColl.name}` : ''}
        footer={
          <View style={{ flexDirection: 'row', gap: 8, justifyContent: 'flex-end' }}>
            <Button onPress={() => setManageColl(null)}>Close</Button>
            <ActionBtn icon="check" disabled={!collRename.trim()} onPress={renameColl}>Rename</ActionBtn>
          </View>
        }
      >
        <View style={{ gap: 14 }}>
          <View style={{ gap: 8 }}>
            <Text style={{ fontSize: fontSize.sm, fontWeight: fontWeight.medium, color: colors.ink2, fontFamily: font.sans }}>Collection name</Text>
            <CommonInput value={collRename} onChangeText={setCollRename} placeholder="Collection name" isDark={isDark} />
          </View>

          {/* Members — in collection order, reorderable */}
          <Text style={{ fontSize: fontSize.sm, fontWeight: fontWeight.medium, color: colors.ink2, fontFamily: font.sans }}>
            In this collection ({manageCollItems.length})
          </Text>
          <View style={{ gap: 6 }}>
            {manageCollItems.length === 0 ? (
              <Text style={{ fontSize: fontSize.sm, color: colors.ink3, fontFamily: font.sans }}>No items yet — add some below.</Text>
            ) : manageCollItems.map((m: any, i: number) => {
              const mid = m.catalogItemId ?? m.id;
              return (
                <View key={mid} style={{ flexDirection: 'row', alignItems: 'center', justifyContent: 'space-between', borderWidth: 1, borderColor: colors.line, borderRadius: radii.r2, paddingVertical: 8, paddingHorizontal: 10 }}>
                  <Text style={{ fontFamily: font.sans, fontSize: fontSize.sm, color: colors.ink }}>{i + 1}. {collItemLabel(mid)}</Text>
                  <View style={{ flexDirection: 'row', alignItems: 'center', gap: 4 }}>
                    <Button kind="ghost" size="xs" iconOnly icon="chev-u" aria-label="Move up" disabled={i === 0} onPress={() => moveCollMember(mid, -1)} />
                    <Button kind="ghost" size="xs" iconOnly icon="chev-d" aria-label="Move down" disabled={i === manageCollItems.length - 1} onPress={() => moveCollMember(mid, 1)} />
                    <Button kind="ghost" size="xs" icon="x" onPress={() => removeItemFromColl(mid)}>Remove</Button>
                  </View>
                </View>
              );
            })}
          </View>

          {/* Add items — everything not already a member */}
          <Text style={{ fontSize: fontSize.sm, fontWeight: fontWeight.medium, color: colors.ink2, fontFamily: font.sans, marginTop: 6 }}>
            Add items
          </Text>
          <View style={{ maxHeight: 240, gap: 6 }}>
            {collAllItems.filter(it => !collMemberIds.has(it.id)).length === 0 ? (
              <Text style={{ fontSize: fontSize.sm, color: colors.ink3, fontFamily: font.sans }}>All items are already in this collection.</Text>
            ) : collAllItems.filter(it => !collMemberIds.has(it.id)).map(it => (
              <View key={it.id} style={{ flexDirection: 'row', alignItems: 'center', justifyContent: 'space-between', borderWidth: 1, borderColor: colors.line, borderRadius: radii.r2, paddingVertical: 8, paddingHorizontal: 10 }}>
                <Text style={{ fontFamily: font.sans, fontSize: fontSize.sm, color: colors.ink }}>{collItemLabel(it.id)}</Text>
                <Button size="xs" icon="plus" onPress={() => addItemToColl(it.id)}>Add</Button>
              </View>
            ))}
          </View>
        </View>
      </Modal>

      {/* Add category modal (also used to add a sub-category under a parent) */}
      <Modal
        open={addingCat}
        onClose={() => setAddingCat(false)}
        size="sm"
        title={catParentId ? `Add sub-category to “${allCategories.find(c => c.id === catParentId)?.name ?? 'category'}”` : 'Add category'}
        footer={
          <View style={{ flexDirection: 'row', gap: 8, justifyContent: 'flex-end' }}>
            <Button onPress={() => setAddingCat(false)}>Cancel</Button>
            <ActionBtn icon="check" disabled={savingCat || !catName.trim()} onPress={addCategory}>
              {savingCat ? 'Adding…' : (catParentId ? 'Add sub-category' : 'Add category')}
            </ActionBtn>
          </View>
        }
      >
        <View style={{ gap: 12 }}>
          <View style={{ gap: 8 }}>
            <Text style={{ fontSize: fontSize.sm, fontWeight: fontWeight.medium, color: colors.ink2, fontFamily: font.sans }}>Category name</Text>
            <CommonInput value={catName} onChangeText={setCatName} placeholder="e.g. Sedans" isDark={isDark} inputProps={{ autoFocus: true, onSubmitEditing: addCategory }} />
          </View>
          <View style={{ gap: 8 }}>
            <Text style={{ fontSize: fontSize.sm, fontWeight: fontWeight.medium, color: colors.ink2, fontFamily: font.sans }}>Parent category (optional)</Text>
            <AppDropdown
              compact
              isDark={isDark}
              placeholder="None — top-level category"
              value={catParentId}
              items={[{ label: 'None — top-level category', value: '' }, ...allCategories.map(c => ({ label: c.name, value: c.id }))]}
              onSelect={setCatParentId}
            />
            <Text style={{ fontSize: fontSize.xs, color: colors.ink3, fontFamily: font.sans }}>
              Pick a parent to make this a sub-category (e.g. Cars → Sedans).
            </Text>
          </View>
        </View>
      </Modal>

      {/* Add tag modal */}
      <Modal
        open={addingTag}
        onClose={() => setAddingTag(false)}
        size="sm"
        title="Add tag"
        footer={
          <View style={{ flexDirection: 'row', gap: 8, justifyContent: 'flex-end' }}>
            <Button onPress={() => setAddingTag(false)}>Cancel</Button>
            <ActionBtn icon="check" disabled={savingTag || !tagName.trim()} onPress={addTag}>
              {savingTag ? 'Adding…' : 'Add tag'}
            </ActionBtn>
          </View>
        }
      >
        <View style={{ gap: 8 }}>
          <Text style={{ fontSize: fontSize.sm, fontWeight: fontWeight.medium, color: colors.ink2, fontFamily: font.sans }}>Tag name</Text>
          <CommonInput value={tagName} onChangeText={setTagName} placeholder="e.g. Featured" isDark={isDark} inputProps={{ autoFocus: true, onSubmitEditing: addTag }} />
        </View>
      </Modal>

      {/* Add field modal */}
      <Modal
        open={addFieldOpen}
        onClose={() => setAddFieldOpen(false)}
        size="sm"
        title="Add field"
        footer={
          <View style={{ flexDirection: 'row', gap: 8, justifyContent: 'flex-end' }}>
            <Button onPress={() => setAddFieldOpen(false)}>Cancel</Button>
            <ActionBtn icon="check" disabled={savingField || !newFieldName.trim()} onPress={addNewField}>
              {savingField ? 'Adding…' : 'Add field'}
            </ActionBtn>
          </View>
        }
      >
        <View style={{ gap: 14 }}>
          <View style={{ gap: 6 }}>
            <Text style={{ fontSize: fontSize.sm, fontWeight: fontWeight.medium, color: colors.ink2, fontFamily: font.sans }}>Field name</Text>
            <CommonInput value={newFieldName} onChangeText={setNewFieldName} placeholder="e.g. Colour" isDark={isDark} inputProps={{ autoFocus: true }} />
          </View>
          <View style={{ gap: 6 }}>
            <Text style={{ fontSize: fontSize.sm, fontWeight: fontWeight.medium, color: colors.ink2, fontFamily: font.sans }}>Field type</Text>
            <AppDropdown
              placeholder="Select type"
              value={newFieldType}
              onSelect={(v: string) => setNewFieldType(v)}
              items={fieldTypes.picker.map((ft) => ({ value: ft.type, label: ft.label }))}
              isDark={isDark}
              compact
            />
          </View>
          <Switch label="Required" checked={newFieldRequired} onChange={() => setNewFieldRequired(v => !v)} />
        </View>
      </Modal>

      {/* Confirm delete category/tag */}
      <ConfirmDialog
        visible={!!delTaxon}
        title={delTaxon?.kind === 'category' ? 'Delete category?' : 'Delete tag?'}
        message={delTaxon ? `"${delTaxon.name}" will be permanently removed from the catalog. This can't be undone.` : ''}
        confirmText={deletingTaxon ? 'Deleting…' : 'Delete'}
        cancelText="Cancel"
        onConfirm={confirmDeleteTaxon}
        onCancel={() => setDelTaxon(null)}
        onClose={() => setDelTaxon(null)}
      />

      {/* Confirm delete a field from the type */}
      <ConfirmDialog
        visible={!!delField}
        title="Delete field?"
        message={delField ? `Field "${delField}" and its stored values will be permanently removed from this type (affects all items of this type).` : ''}
        confirmText={deletingField ? 'Deleting…' : 'Delete'}
        cancelText="Cancel"
        onConfirm={confirmDeleteField}
        onCancel={() => setDelField(null)}
        onClose={() => setDelField(null)}
      />

      {/* Add a dropdown option value to an ENUM/MULTI_SELECT field */}
      <Modal
        open={!!addOptionFor}
        onClose={() => setAddOptionFor(null)}
        size="sm"
        title="Add dropdown option"
        footer={
          <View style={{ flexDirection: 'row', gap: 8, justifyContent: 'flex-end' }}>
            <Button onPress={() => setAddOptionFor(null)}>Cancel</Button>
            <ActionBtn icon="check" disabled={savingOption || !optionValue.trim()} onPress={addFieldOption}>
              {savingOption ? 'Adding…' : 'Add option'}
            </ActionBtn>
          </View>
        }
      >
        <View style={{ gap: 8 }}>
          <Text style={{ fontSize: fontSize.sm, fontWeight: fontWeight.medium, color: colors.ink2, fontFamily: font.sans }}>Option value</Text>
          <CommonInput value={optionValue} onChangeText={setOptionValue} placeholder="e.g. Red" isDark={isDark} inputProps={{ autoFocus: true, onSubmitEditing: addFieldOption }} />
        </View>
      </Modal>

      {/* Confirm remove a dropdown option */}
      <ConfirmDialog
        visible={!!delOption}
        title="Delete option?"
        message={delOption ? `Option "${delOption.opt}" will be removed from "${delOption.fieldKey}". Items using it will lose this value.` : ''}
        confirmText={removingOption ? 'Deleting…' : 'Delete'}
        cancelText="Cancel"
        onConfirm={confirmRemoveOption}
        onCancel={() => setDelOption(null)}
        onClose={() => setDelOption(null)}
      />

      {/* Add bundle component — pick item + relation type + quantity */}
      <Modal
        open={addCompOpen}
        onClose={() => setAddCompOpen(false)}
        size="sm"
        title="Add component"
        footer={
          <View style={{ flexDirection: 'row', gap: 8, justifyContent: 'flex-end' }}>
            <Button onPress={() => setAddCompOpen(false)}>Cancel</Button>
            <ActionBtn icon="plus" disabled={!pickItemId || addingComponent} onPress={addBundleComponent}>
              {addingComponent ? 'Adding…' : 'Add'}
            </ActionBtn>
          </View>
        }
      >
        <View style={{ gap: 12 }}>
          {pickerItems.length === 0 ? (
            <View style={{ borderWidth: 1, borderColor: colors.line, borderRadius: radii.r2, padding: 12, backgroundColor: colors.paper2 }}>
              <Text style={{ fontSize: fontSize.sm, color: colors.ink2, fontFamily: font.sans }}>
                No other items to add. A bundle needs at least one item besides this one — create another item first, then add it here.
              </Text>
            </View>
          ) : (
            <>
              <View style={{ gap: 6 }}>
                <Text style={{ fontSize: fontSize.sm, fontWeight: fontWeight.medium, color: colors.ink2, fontFamily: font.sans }}>Component item</Text>
                <AppDropdown compact placeholder="Select component…" value={pickItemId} items={pickerItems} onSelect={setPickItemId} isDark={isDark} />
              </View>
              <View style={{ gap: 6 }}>
                <Text style={{ fontSize: fontSize.sm, fontWeight: fontWeight.medium, color: colors.ink2, fontFamily: font.sans }}>Relation</Text>
                <AppDropdown compact placeholder="Relation" value={bundleRelation} items={RELATIONS.map(r => ({ label: r, value: r }))} onSelect={(v: string) => setBundleRelation(v as RelationType)} isDark={isDark} />
              </View>
              <View style={{ gap: 6 }}>
                <Text style={{ fontSize: fontSize.sm, fontWeight: fontWeight.medium, color: colors.ink2, fontFamily: font.sans }}>Quantity</Text>
                <CommonInput value={bundleQty} onChangeText={(t) => setBundleQty(t.replace(/[^0-9]/g, '').slice(0, 4))} placeholder="e.g. 1" isDark={isDark} inputProps={{ keyboardType: 'numeric' }} />
              </View>
            </>
          )}
        </View>
      </Modal>

      {/* Edit bundle component — relation + quantity */}
      <Modal
        open={!!editComp}
        onClose={() => setEditComp(null)}
        size="sm"
        title="Edit component"
        footer={
          <View style={{ flexDirection: 'row', gap: 8, justifyContent: 'flex-end' }}>
            <Button onPress={() => setEditComp(null)}>Cancel</Button>
            <ActionBtn icon="check" disabled={savingEditComp} onPress={saveEditComponent}>
              {savingEditComp ? 'Saving…' : 'Save'}
            </ActionBtn>
          </View>
        }
      >
        <View style={{ gap: 12 }}>
          <View style={{ gap: 6 }}>
            <Text style={{ fontSize: fontSize.sm, fontWeight: fontWeight.medium, color: colors.ink2, fontFamily: font.sans }}>Component</Text>
            <Text style={{ fontSize: fontSize.sm, color: colors.ink, fontFamily: font.sans }}>{editComp ? labelForItem(editComp.childItemId) : ''}</Text>
          </View>
          <View style={{ gap: 6 }}>
            <Text style={{ fontSize: fontSize.sm, fontWeight: fontWeight.medium, color: colors.ink2, fontFamily: font.sans }}>Relation</Text>
            <AppDropdown compact placeholder="Relation" value={editCompRelation} items={RELATIONS.map(r => ({ label: r, value: r }))} onSelect={(v: string) => setEditCompRelation(v as RelationType)} isDark={isDark} />
          </View>
          <View style={{ gap: 6 }}>
            <Text style={{ fontSize: fontSize.sm, fontWeight: fontWeight.medium, color: colors.ink2, fontFamily: font.sans }}>Quantity</Text>
            <CommonInput value={editCompQty} onChangeText={(t) => setEditCompQty(t.replace(/[^0-9]/g, '').slice(0, 4))} placeholder="e.g. 1" isDark={isDark} inputProps={{ keyboardType: 'numeric' }} />
          </View>
        </View>
      </Modal>

      {/* Confirm remove a bundle component */}
      <ConfirmDialog
        visible={!!delComponent}
        title="Remove component?"
        message={delComponent ? `"${delComponent.label}" will be removed from this bundle.` : ''}
        confirmText={removingComponent ? 'Removing…' : 'Remove'}
        cancelText="Cancel"
        onConfirm={confirmRemoveComponent}
        onCancel={() => setDelComponent(null)}
        onClose={() => setDelComponent(null)}
      />

      {/* Confirm clear media — on confirm the asset is removed and the row resets */}
      <ConfirmDialog
        visible={!!clearMediaKey}
        title="Clear media?"
        message={clearMediaKey ? `This removes the uploaded media for "${clearMediaKey}". You can upload a new one afterwards.` : ''}
        confirmText="Clear"
        cancelText="Cancel"
        onConfirm={async () => { const k = clearMediaKey; setClearMediaKey(null); if (k) await clearMedia(k); }}
        onCancel={() => setClearMediaKey(null)}
        onClose={() => setClearMediaKey(null)}
      />

      {/* Rename category / tag (the edit action) */}
      <Modal
        open={!!editTaxon}
        onClose={() => setEditTaxon(null)}
        size="sm"
        title={editTaxon?.kind === 'category' ? 'Rename category' : 'Rename tag'}
        footer={
          <View style={{ flexDirection: 'row', gap: 8, justifyContent: 'flex-end' }}>
            <Button onPress={() => setEditTaxon(null)}>Cancel</Button>
            <ActionBtn icon="check" disabled={savingTaxonEdit || !editTaxonName.trim()} onPress={saveTaxonEdit}>
              {savingTaxonEdit ? 'Saving…' : 'Save'}
            </ActionBtn>
          </View>
        }
      >
        <View style={{ gap: 8 }}>
          <Text style={{ fontSize: fontSize.sm, fontWeight: fontWeight.medium, color: colors.ink2, fontFamily: font.sans }}>Name</Text>
          <CommonInput value={editTaxonName} onChangeText={setEditTaxonName} placeholder="New name" isDark={isDark} inputProps={{ autoFocus: true, onSubmitEditing: saveTaxonEdit }} />
        </View>
      </Modal>
    </ScrollView>
  );
}
